import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { resolveShippingRegion, resolveNearestAddress } from '@theme/province-resolver';
import { resolveCurrentLocation } from '@theme/shopla-current-location';

/**
 * localStorage key holding the visitor's search preferences — the active mode,
 * the resolved current location (`l`), the shipping zone (`ships_to`) and the
 * shipping region's display name (what "Ships to {region}" lines show) — so search
 * behaves consistently as they move between pages, including product pages
 * that have no map of their own. (Older versions persisted a `center` — the
 * map center must never determine the location, so it is ignored.)
 */
const STORAGE_KEY = 'shopla:map-search';

/**
 * How authoritative each current-location source is. A non-explicit update
 * (the page-load resolution) only replaces a location from a less- or
 * equally-ranked source — so a late GeoIP response never clobbers a
 * granted-geolocation fix, and neither overrides a manual choice.
 */
const LOCATION_SOURCE_RANK = { ip: 1, geolocation: 2, manual: 3 };

/**
 * Wraps a longitude into [-180, 180) — Mapbox viewport bounds extend past
 * ±180 when the view crosses the antimeridian.
 *
 * @param {number} lng
 * @returns {number}
 */
const wrapLongitude = (lng) => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * Returns the collection-scoped form of a bare product URL —
 * `/products/<handle>` → `/collections/<collection>/products/<handle>`, matching the
 * Liquid `within` filter — so PICKUP results (shown as part of a shop-location
 * collection) link into that collection and the product page opens with the location
 * context. Shipping-mode cards deliberately never go through this: they keep the bare
 * URL, whose product page shows the delivery context instead. Any locale/path prefix
 * is preserved. Returns the URL unchanged when it isn't a bare product URL, is already
 * collection-scoped, or no collection handle is given.
 *
 * @param {string | null} productUrl - The bare product URL (a resource-card link href).
 * @param {string} collectionHandle - The shop-location collection handle to scope into.
 * @returns {string | null} The scoped, root-relative product URL.
 */
const scopeProductUrl = (productUrl, collectionHandle) => {
  if (!productUrl || !collectionHandle) return productUrl;
  try {
    const url = new URL(productUrl, window.location.origin);
    const at = url.pathname.indexOf('/products/');
    if (at === -1 || url.pathname.includes('/collections/')) return productUrl;
    url.pathname = `${url.pathname.slice(0, at)}/collections/${collectionHandle}${url.pathname.slice(at)}`;
    return url.pathname + url.search + url.hash;
  } catch (error) {
    return productUrl;
  }
};

/**
 * The section that renders ONE product's card for the map
 * (sections/shopla-product-card.liquid), fetched on the product's own route.
 */
const PRODUCT_CARD_SECTION = 'shopla-product-card';

/**
 * The storefront's locale path prefix — "" on the primary language, "/de" and
 * the like on a secondary one. Derived from the theme's own search route, which
 * Liquid renders with the active locale, so a product fetched here lands in the
 * same language as the page that asked for it.
 *
 * `Theme` is a global lexical binding (not a window property) defined in the
 * theme's inline script; guard against load-order edge cases.
 *
 * @returns {string} The prefix, with no trailing slash.
 */
const localeRoot = () => {
  const searchUrl = (typeof Theme !== 'undefined' && Theme.routes?.search_url) || '/search';
  const at = searchUrl.lastIndexOf('/search');
  return at > 0 ? searchUrl.slice(0, at) : '';
};

/** The magnifier shown above an empty result's message. Same drawing as the
 *  search bar's icon-search.svg, with the stroke width fixed rather than read
 *  from the theme's --icon-stroke-width: this markup is injected, not rendered
 *  through Liquid's inline_asset_content. Mirrored in mapbox.js. */
const SEARCH_ICON_SVG =
  '<svg viewBox="0 0 20 20" width="28" height="28" fill="none" aria-hidden="true" focusable="false"><circle cx="9" cy="9" r="4.75" stroke="currentColor" stroke-width="1.5"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m12.5 12.5 3.25 3.25"/></svg>';

/**
 * Builds the shop-registration prompt shown under an empty search result —
 * "Own a shop? <a>Register it.</a>" — as a paragraph. Mirrored in mapbox.js
 * for the map's results pane, with matching class names.
 *
 * @param {{ prompt?: string, cta?: string, url: string }} register
 * @param {string} className
 * @returns {HTMLParagraphElement}
 */
function buildRegisterPrompt({ prompt, cta, url }, className) {
  const paragraph = document.createElement('p');
  paragraph.className = className;
  if (prompt) paragraph.appendChild(document.createTextNode(`${prompt} `));
  const link = document.createElement('a');
  link.href = url;
  link.textContent = cta || url;
  paragraph.appendChild(link);
  return paragraph;
}

/**
 * How many (collapsed) products the global map search asks for. Larger than the
 * service default so enough distinct shop locations surface to populate the
 * results pane (the pane lists a card per location, in first-appearance order).
 */
const GLOBAL_SIZE = 30;

/**
 * How many products a shop location's in-map carousel loads per page. The
 * carousel shows at most this many at once; its right arrow fetches the next
 * page (see `loadLocationProducts`).
 */
const CAROUSEL_PAGE = 3;

/**
 * How many shop locations (with their cached carousel products) the
 * per-context result cache may hold. Beyond it the least-recently-seen
 * location is evicted — panning far and wide can't grow memory unboundedly.
 */
const MAX_CACHED_LOCATIONS = 100;

/**
 * The whole-world `bb` box (`"top,left,bottom,right"`) used for the very
 * first idle-pins `/locations` listing, before the map has reported its
 * viewport. A collection page's pins start hidden (`data-pins-deferred`)
 * until that listing arrives, so it must not wait for the map to load —
 * and the listing is bounded (one row per distinct location) and tiny.
 */
const WORLD_BOUNDS = '90.000000,-180.000000,-90.000000,180.000000';

/**
 * How many place handles one `/places` lookup may name. Matches the service's
 * MAX_PLACE_HANDLES, which rejects a longer list rather than truncating it, so
 * a viewport holding more locations than this is chunked instead of refused.
 */
const PLACE_LOOKUP_CHUNK = 250;

/**
 * The rejection a SUPERSEDED carousel load settles with — the same AbortError a
 * `fetch` aborted mid-flight rejects with, so the map has one thing to check.
 * A load is superseded when a newer one starts, when the search context
 * changes under it (a keystroke, a mode or location change — see `#runSearch`)
 * or when the query is cleared. It then has no answer, and must never resolve
 * to an empty page: the map renders an empty page as "No products found",
 * which is exactly what a carousel still waiting on its first page used to
 * flash on every keystroke that landed mid-load.
 *
 * @returns {DOMException}
 */
const supersededError = () => new DOMException('The carousel load was superseded by a newer one', 'AbortError');

/**
 * A standalone search box for the map — a *controller*.
 *
 * It owns the search query and the active search mode (`location` vs.
 * `shipping`) and runs live product search against the shopla-search service,
 * rendering the result cards through Shopify's `predictive-search` section.
 *
 * It also owns the visitor's CURRENT LOCATION — which is never the map
 * center. It is resolved from, in order of authority:
 *   1. an explicit right-click "Set as current location" on the map
 *      (`map:location-changed` with source `manual`),
 *   2. the browser Geolocation API — the map's geolocate control, or a silent
 *      read on load when permission was already granted (never prompts),
 *   3. the GeoIP approximation from the search service (`/geoip`) as the
 *      fallback.
 * The location is sent as `l=lat,lng` so results carry `_distance`; it never
 * ranks them.
 *
 * It is decoupled from the map. When a `<mapbox-component>` is present on the
 * page, the bar hands the map the ordered set of shop locations that matched
 * (via the bubbling `shopla:search:results` event) — the map renders a card per
 * location in its results pane (left on desktop, bottom on mobile, and in
 * fullscreen) and shows a product carousel above the selected location's pin.
 * The map lazily loads each location's products by calling back the bar's
 * `loadLocationProducts(handle, from, size)` method (a `/search` scoped to the
 * location handle, paginated). With no map present the bar falls back to
 * rendering product cards in its own dropdown. The map reports its visible
 * viewport via `map:bounds-changed`; the bar expands it to at least twice the
 * visible area and sends it as the `bb` bounding-box FILTER for location
 * search — never ranking by distance.
 *
 * Panning is ADDITIVE: while only the bounding box changes, fresh results are
 * merged into an in-memory cache keyed by shop location — capped at
 * MAX_CACHED_LOCATIONS, least-recently-seen evicted first — and the union is
 * handed to the map, so pins/cards accumulate. Each cached location also
 * keeps the carousel product pages loaded for it, so reopening a carousel
 * (e.g. after panning away and back) costs no request. Any change to the
 * search context — q, mode, filters or location — empties the cache (it does
 * not survive a page refresh).
 *
 * While NO search is ongoing the bar drives the map's IDLE pins instead
 * (`shopla:pins:changed`): on a collection page it lists — via the search
 * service's query-less `/locations` endpoint — the shop locations inside the
 * viewport that carry at least one product of the collection, so only those
 * stay pinned; on pages without a collection context every pin shows. See
 * `#refreshIdlePins`.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLButtonElement} locationButton - The location-search ("Pickup") mode button.
 * @property {HTMLButtonElement} shippingButton - The shipping-search ("Shipping") mode button.
 * @property {HTMLElement} [results] - The fallback results dropdown (no-map pages).
 * @property {HTMLElement} [pickupPopover] - The Pickup pill's location popover.
 * @property {HTMLElement} [pickupAddress] - The Pickup popover's closest-address line.
 * @property {HTMLButtonElement} [pickupLocateButton] - The Pickup popover's precise-geolocation action.
 * @property {HTMLElement} [pickupError] - The Pickup popover's geolocation-failure notice.
 * @property {HTMLElement} [pickupHint] - The Pickup popover's right-click-on-the-map tip (desktop, map pages).
 * @property {HTMLElement} [deliveryPopover] - The Shipping pill's address popover.
 * @property {HTMLButtonElement[]} [deliveryAddressButtons] - The signed-in account-address rows.
 * @property {HTMLElement} [deliveryAddress] - The signed-out popover's location-derived address line.
 * @property {HTMLButtonElement} [deliveryLocateButton] - The signed-out popover's precise-geolocation action.
 * @property {HTMLElement} [deliveryError] - The signed-out popover's geolocation-failure notice.
 *
 * @extends {Component<Refs>}
 */
export class MapSearchComponent extends Component {
  requiredRefs = ['searchInput', 'locationButton', 'shippingButton'];

  /**
   * The active search mode. It selects the filter exclusively, with or
   * without a map: `location` sends the current location (plus the `bb`
   * viewport box when a map is on the page), `shipping` sends `ships_to`
   * alone — never `l`, which would drop mail-order products. Persisted
   * across navigations.
   *
   * @type {'location' | 'shipping'}
   */
  #mode = 'location';

  /**
   * The visitor's current location and how it was determined — never the map
   * center. Sent as `l` so results carry `_distance` (it never ranks them),
   * and the origin for the province-level ships_to default. Persisted so
   * map-less pages (e.g. product pages) share it.
   *
   * @type {{ lat: number, lng: number, source: 'manual' | 'geolocation' | 'ip' } | null}
   */
  #location = null;

  /**
   * The map's current visible viewport, reported live via
   * `map:bounds-changed`. Expanded (2× area) into the `bb` filter for
   * location-mode search. Not persisted — it only means something while a map
   * is on the page.
   *
   * @type {{ north: number, south: number, east: number, west: number } | null}
   */
  #bounds = null;

  /**
   * The cached map results of the current search context, keyed by shop
   * location handle in first-appearance (ranked) order — in-memory only,
   * deliberately not persisted. While only the bounding box changes (the map
   * is panned/zoomed), fresh results are merged in, so results ADD to the
   * map; any change to the context (q, mode, filters, location) empties it.
   * Each bucket also caches the location's loaded carousel product pages
   * (keyed "from:size") and carries a recency stamp for the
   * MAX_CACHED_LOCATIONS eviction.
   *
   * @type {Map<string, { handle: string, distance: number | null, lastSeen: number, pages: Map<string, { products: { id: string, html: string, distance: number | null }[], hasMore: boolean }> }>}
   */
  #locationCache = new Map();

  /** Monotonic stamp for the cache's least-recently-seen eviction. */
  #cacheSeq = 0;

  /**
   * The delivery-only results of the current search — the hits that came back
   * with a blank `shop_location_handle` because their product has no pickup
   * location anywhere (the indexer writes them as a single location-less doc).
   * They can't be placed on the map, so the map renders them as one "Shipping
   * only" card whose carousel floats in the middle instead of over a pin.
   *
   * Only ever populated in "Shipping" mode: a location search sends `l` / `bb`,
   * which the service uses to filter location-less docs out server-side, so
   * there is nothing to collect. Replaced wholesale on each search rather than
   * accumulated — unlike the per-location results these don't depend on the
   * map's viewport, so panning can't add to them.
   *
   * Keyed by SHOP handle in first-appearance (ranked) order — one entry per
   * shop with delivery-only matches, which is one card in the pane. Hits whose
   * shop is unknown (a search service predating `shop_handle`) collect under
   * "" and render as a single unbranded card.
   *
   * @type {Map<string, { id: string, handle: string, shop: string, distance: number | null }[]>}
   */
  #deliveryEntries = new Map();

  /** Places resolved from the search service's `/places` lookup, keyed by
   *  handle — shops (what a "Shipping only" card is rendered from) and shop
   *  locations (what a pin and its card are rendered from) in one cache, since
   *  a handle is unique across a store's collections. `null` marks a handle the
   *  service knows nothing about, so it isn't asked for again.
   *
   *  Never cleared with the per-context caches below — a merchant's name, logo,
   *  cover and address don't change because the visitor typed another letter,
   *  and the same places recur across searches and pans.
   *  @type {Map<string, Record<string, any> | null>} */
  #places = new Map();

  /** Rendered card pages of the delivery-only carousels, keyed
   *  "<shop>|<from>:<size>" — the same page cache the per-location carousels
   *  keep, so paging back and forth (or reopening a card) costs no request.
   *  @type {Map<string, { products: { id: string, html: string, distance: number | null }[], hasMore: boolean }>} */
  #deliveryPages = new Map();

  /** `bb` boxes already fetched for the current context — an identical box is
   *  served from the location cache without re-querying. @type {Set<string>} */
  #fetchedBoxes = new Set();

  /** The context key the accumulation belongs to (see #searchContextKey). */
  #contextKey = '';

  /** Expanded `bb` boxes already listed for the current IDLE-pins context — an
   *  identical box is served from #idleHandles without re-querying.
   *  @type {Set<string>} */
  #idleBoxes = new Set();

  /** Shop-location handles accumulated for the current idle-pins context (the
   *  no-search state of a collection page): every location inside the boxes
   *  listed so far that carries a product of the context collection.
   *  @type {Set<string>} */
  #idleHandles = new Set();

  /** The context collection the idle-pins accumulation belongs to — removing
   *  the chip (or navigating) resets it. `''` is the no-collection context (a
   *  page listing every location in view); `null` is "nothing listed yet".
   *  @type {string | null} */
  #idleCollection = null;

  /**
   * The shipping zone sent as `ships_to` in "Shipping" mode. Starts as the
   * storefront's selected country (#country) and is upgraded to the visitor's
   * ISO 3166-2 province (e.g. "US-CA") once resolved from their location via
   * Mapbox — unless an account shipping address is selected (#delivery),
   * which then owns the zone. Persisted.
   *
   * @type {string}
   */
  #shipsTo = '';

  /**
   * The shipping region's display name — the human-readable form of #shipsTo
   * that the delivery-mode "Ships to {region}" lines show (map cards via the
   * results event, product pages via the persisted copy + the
   * `shopla:ships-to:changed` event). Follows the zone's own two sources: the
   * selected account shipping address's province/country (data-region), else
   * the region resolved from the current location together with the zone code
   * (see #doResolveShipsTo). `''` while unknown — the consumers then omit the
   * line (or keep their server-rendered country fallback). Persisted.
   *
   * @type {string}
   */
  #shipsToRegion = '';

  /**
   * The explicitly selected shipping address (the Shipping popover, signed
   * in): one of the account's saved addresses, whose ISO 3166-2 zone owns
   * #shipsTo — the automatic location-based province resolution stands down
   * while it is set. `null` means automatic (the location-derived default).
   * Persisted, but only restored while signed in AND the address still
   * matches a rendered account address (deleted addresses self-heal to
   * automatic, edited ones re-derive their zone from the fresh markup).
   *
   * @type {{ source: 'account', id: string, shipsTo: string } | null}
   */
  #delivery = null;

  /**
   * The storefront's selected ISO 3166-1 country (data-ships-to, upper-cased).
   * The ships_to baseline and the country a resolved province must belong to.
   *
   * @type {string}
   */
  #country = '';

  /**
   * Mapbox public token (data-mapbox-token) used to resolve the province
   * default. Without it the country is used as-is.
   *
   * @type {string}
   */
  #mapboxToken = '';

  /**
   * The last (country|lat|lng) key resolved, so panning within the same
   * rounded coordinate doesn't re-hit the Mapbox geocoder.
   *
   * @type {string | null}
   */
  #resolvedKey = null;

  /**
   * The context collection handle, if any: a collection page's collection, or —
   * on a product page — the product's shop collection (`shopla.shop`). Sent as
   * the `coll` hard filter and shown as the removable chip. Not persisted — it
   * is derived from the current page.
   *
   * @type {string | null}
   */
  #collection = null;

  #controller = new AbortController();

  /** Aborts the in-flight search when a newer one starts. @type {AbortController | null} */
  #searchController = null;

  /** Aborts the in-flight per-location product load (a carousel page) when a
   *  newer one starts or a fresh global search supersedes it. @type {AbortController | null} */
  #locationController = null;

  /** Aborts the in-flight idle-pins listing when a newer one starts.
   *  @type {AbortController | null} */
  #idleController = null;

  /** Aborts the in-flight province resolution when a newer one starts (keeps a
   *  slow, stale geocode from clobbering a fresher location). @type {AbortController | null} */
  #resolveController = null;

  /** Aborts the in-flight reverse-geocode of the pickup popover's address
   *  line when a newer one starts. @type {AbortController | null} */
  #addressController = null;

  /** The last coordinate reverse-geocoded for the pickup popover, with its
   *  display text — reopening the popover at an unchanged location costs no
   *  request. @type {{ key: string, text: string } | null} */
  #resolvedAddress = null;

  /** True while the popover's precise-geolocation request is running (the
   *  locate button ignores clicks meanwhile). */
  #locatePending = false;

  /** Body-level tooltip element for the icon-only mobile mode pills (a portal —
   *  see `#setupModeTips`). @type {HTMLElement | null} */
  #modeTip = null;

  connectedCallback() {
    super.connectedCallback();

    // Restore the visitor's persisted preferences so search behaves the same
    // across pages — crucially on product pages, which have no map of their
    // own. A legacy persisted `center` (an old map center) is deliberately
    // ignored: the map center never determines the current location.
    const persisted = this.#readPersisted();
    if (persisted.mode === 'location' || persisted.mode === 'shipping') this.#mode = persisted.mode;
    if (this.#isLocation(persisted.location)) this.#location = persisted.location;

    // The selected-country baseline and the Mapbox token used to upgrade it to
    // a province default.
    this.#country = (this.dataset.shipsTo || '').toUpperCase();
    this.#mapboxToken = this.dataset.mapboxToken || '';

    // Shipping zone: a persisted choice wins — but only if it still belongs to
    // the selected country (the visitor may have switched country since) — else
    // the country. A province default is then resolved from their location.
    // The region's display name travels with the zone: restored only while the
    // zone itself is (a dropped zone would leave it naming a region in a
    // country the search no longer ships to); re-resolved otherwise.
    const persistedShipsTo = (persisted.shipsTo || '').toUpperCase();
    if (persistedShipsTo && this.#belongsToCountry(persistedShipsTo)) {
      this.#shipsTo = persistedShipsTo;
      if (typeof persisted.shipsToRegion === 'string') this.#shipsToRegion = persisted.shipsToRegion;
      // The location + country the zone was resolved from travels with it, so
      // a later page with the same location skips the Mapbox reverse geocode
      // (a billable request) instead of repeating it on every page view.
      if (typeof persisted.shipsToKey === 'string') this.#resolvedKey = persisted.shipsToKey;
    } else {
      this.#shipsTo = this.#country;
    }

    // A persisted account shipping address overrides the zone above — it was
    // an explicit choice, so it skips the selected-country guard (the address
    // may legitimately be in another country). Only restored while it still
    // matches a rendered account address; see #restoreDelivery.
    this.#restoreDelivery(persisted.delivery);

    // The context collection (a collection page, or a product's shop
    // collection) — sent as the `coll` hard filter and shown as the chip.
    this.#collection = this.dataset.collection || null;

    // Reflect the restored mode on the toggle buttons, then persist the
    // resolved state (covers first visits and the zone fallback above).
    this.#reflectMode();
    this.#persist();

    const { signal } = this.#controller;
    // The map reports its visible viewport (expanded into the `bb` filter for
    // location search) and explicit current-location changes (the geolocate
    // control, the right-click "Set as current location").
    document.addEventListener('map:bounds-changed', this.#handleBoundsChanged, { signal });
    document.addEventListener('map:location-changed', this.#handleLocationChanged, { signal });
    // Dismiss the fallback dropdown when clicking outside the search box.
    document.addEventListener('click', this.#handleDocumentClick, { signal });

    // Wire up the icon-only mobile mode pills' hover/tap tooltips.
    this.#setupModeTips(signal);

    // Mode popovers (Pickup location / Shipping address): each pill's
    // popovertarget toggles its popover natively (popover="auto" adds light
    // dismiss + Escape, and opening one auto-closes the other); we fill and
    // position them when they open. A fixed-position panel can't follow its
    // pill once the page scrolls or resizes, so those close it (mirroring
    // the mode tip) — except scrolls inside a popover itself (the address
    // list scrolls).
    const { pickupPopover, deliveryPopover } = this.refs;
    pickupPopover?.addEventListener('beforetoggle', this.#handlePickupToggle, { signal });
    deliveryPopover?.addEventListener('beforetoggle', this.#handleDeliveryToggle, { signal });
    if (pickupPopover || deliveryPopover) {
      window.addEventListener('resize', this.#closeModePopovers, { signal });
      document.addEventListener('scroll', this.#handleDocumentScroll, { signal, passive: true, capture: true });
    }

    // Resolve the visitor's current location (silent geolocation when already
    // granted, else GeoIP) and a province-level ships_to default from it.
    this.#resolveLocation();
    this.#resolveShipsTo();

    // Seed the idle pins right away where the listing is collection-scoped —
    // a collection page's pins start hidden (data-pins-deferred) until it
    // arrives, and it is bounded by the collection, not the viewport, so
    // there is nothing to wait for. Without a collection the listing IS the
    // viewport's, so it waits for the map to report its bounds
    // (#handleBoundsChanged) and runs exactly once for the initial view.
    if (!this.query && this.#collection) this.#refreshIdlePins();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#searchController?.abort();
    this.#locationController?.abort();
    this.#idleController?.abort();
    this.#resolveController?.abort();
    this.#addressController?.abort();
    this.#modeTip?.remove();
    this.#modeTip = null;
  }

  /**
   * On the mobile layout the two mode pills are icon-only; their labels are
   * shown as a small tooltip below the pressed/hovered pill.
   *
   * The tooltip is rendered at the <body> level (a portal) and positioned with
   * `position: fixed` so it escapes the header's `contain: layout` and every
   * ancestor stacking context / `overflow` — an in-flow tooltip gets painted
   * under the section that follows the header (e.g. the map hero). It follows
   * focus (a tap focuses the pill — see `useLocationMode`/`useShippingMode`) and,
   * for mouse users, hover. Desktop keeps the inline labels, so it's a no-op
   * there (guarded by the media query on show).
   *
   * @param {AbortSignal} signal - Ties the listeners to the component lifecycle.
   */
  #setupModeTips(signal) {
    const tip = document.createElement('div');
    tip.className = 'map-search__mode-tip';
    tip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tip);
    this.#modeTip = tip;

    const { locationButton, shippingButton } = this.refs;
    for (const button of [locationButton, shippingButton]) {
      button.addEventListener('focus', () => this.#showModeTip(button), { signal });
      button.addEventListener('blur', this.#hideModeTip, { signal });
      // Hover only for a real mouse — touch drives it through focus/blur instead,
      // else a tap would flash the tip open then shut on pointerleave.
      button.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.#showModeTip(button);
        },
        { signal }
      );
      button.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'mouse') this.#hideModeTip();
        },
        { signal }
      );
    }

    // A fixed tooltip can't follow the pill once the page scrolls, so drop it.
    document.addEventListener('scroll', this.#hideModeTip, { signal, passive: true, capture: true });
  }

  /**
   * Positions the mode tooltip centered just below the given pill and shows it.
   * Only on the mobile (icon-only) layout — on desktop the pills keep their
   * inline labels, so there's nothing to reveal.
   *
   * @param {HTMLButtonElement} button
   */
  #showModeTip = (button) => {
    const tip = this.#modeTip;
    if (!tip) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    // A pill's popover opens right below the pill — while it is open (or
    // opening) the tooltip would sit on top of it.
    if (button === this.refs.locationButton && this.#isPopoverOpen(this.refs.pickupPopover)) return;
    if (button === this.refs.shippingButton && this.#isPopoverOpen(this.refs.deliveryPopover)) return;

    const text = button.querySelector('.map-search__mode-label')?.textContent?.trim();
    if (!text) return;

    const rect = button.getBoundingClientRect();
    tip.textContent = text;
    tip.style.left = `${rect.left + rect.width / 2}px`;
    tip.style.top = `${rect.bottom + 6}px`;
    tip.classList.add('is-visible');
  };

  /** Hides the mode tooltip. */
  #hideModeTip = () => {
    this.#modeTip?.classList.remove('is-visible');
  };

  /** The active search mode. */
  get mode() {
    return this.#mode;
  }

  /** The current, trimmed search query. */
  get query() {
    return this.refs.searchInput.value.trim();
  }

  /** Whether a map is present to render results in its pane. */
  get #mapPresent() {
    return document.querySelector('mapbox-component') != null;
  }

  /**
   * Whether search results render in the map's pane. On a shop-location
   * collection page (`data-location-page`, see map-search-bar.liquid) the
   * location context is fixed — every result sits at this one location — so
   * the bar uses its own product-card dropdown (like product pages) even
   * though a map is on the page.
   */
  get #useMapResults() {
    return this.#mapPresent && !this.hasAttribute('data-location-page');
  }

  /** Debounced live search as the visitor types. */
  handleInput = debounce(() => {
    // Mirror the predictive-search "Predictive Search" event for the shopla
    // search service: report the typed query (and active mode) to Plausible.
    // Only on a real query — clearing the box just clears results, it isn't a
    // search. A no-op when Plausible isn't loaded (admins, theme editor, or an
    // unset pixel), thanks to the optional call.
    const query = this.query;
    if (query) {
      window.plausible?.('Predictive Shopla Search', { props: { searchQuery: query, mode: this.#mode } });
    }
    this.#runSearch();
  }, 300);

  /** Re-open the fallback dropdown on focus when there is already content. */
  handleFocus = () => {
    if (this.#useMapResults) return;
    const { results } = this.refs;
    if (results && this.query && results.hasChildNodes()) results.hidden = false;
  };

  /**
   * Clears the query (the trailing X button), returns focus to the input, and
   * runs an empty search — which supersedes any in-flight request, clears the
   * results, and restores the idle pins. The button hides itself once the input
   * is empty (CSS :placeholder-shown), so no explicit toggle is needed.
   */
  clearQuery = () => {
    const { searchInput } = this.refs;
    searchInput.value = '';
    searchInput.focus();
    this.#runSearch();
  };

  /** Selects location-based ("Pickup") search. The pill's popovertarget also
   *  toggles the pickup location popover — see #handlePickupToggle. */
  useLocationMode = () => {
    // Focus the button so its icon-only mobile tooltip shows on tap (some
    // touch browsers, e.g. iOS Safari, don't focus a <button> on tap).
    this.refs.locationButton.focus();
    this.#setMode('location');
  };

  /** Selects shipping-based ("Shipping") search. */
  useShippingMode = () => {
    this.refs.shippingButton.focus();
    this.#setMode('shipping');
  };

  /**
   * Whether a mode popover is currently open. The popover polyfill flags the
   * open state with a literal `:popover-open` CLASS — and `matches()` throws
   * on the pseudo-class where the Popover API isn't implemented — so both
   * forms are checked.
   *
   * @param {HTMLElement | undefined} popover
   * @returns {boolean}
   */
  #isPopoverOpen(popover) {
    if (!popover) return false;
    try {
      if (popover.matches(':popover-open')) return true;
    } catch {
      // Pseudo-class unsupported — the polyfill's class check below decides.
    }
    return popover.classList.contains(':popover-open');
  }

  /** Whether either mode popover (Pickup location / Shipping address) is open. */
  #anyModePopoverOpen() {
    return this.#isPopoverOpen(this.refs.pickupPopover) || this.#isPopoverOpen(this.refs.deliveryPopover);
  }

  /**
   * Handles the pickup popover opening (the Pickup pill's popovertarget
   * toggles it; `popover="auto"` supplies light dismiss and Escape). On open:
   * drop the pill's mobile tooltip (the two would overlap), show the
   * right-click tip only when there is a map to right-click, clear a stale
   * geolocation error, refresh the address line, and position the panel
   * under the pill — synchronously, while the popover is still hidden, so it
   * can never paint unpositioned.
   *
   * @param {Event} event
   */
  #handlePickupToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState !== 'open') return;

    this.#hideModeTip();

    const { pickupHint, pickupError, pickupPopover, locationButton } = this.refs;
    if (pickupHint) pickupHint.hidden = !this.#mapPresent;
    if (pickupError) pickupError.hidden = true;

    this.#refreshCurrentLocationAddress();
    this.#positionModePopover(pickupPopover, locationButton);
  };

  /**
   * Handles the delivery popover opening (the Shipping pill's popovertarget).
   * On open: drop the pill's mobile tooltip, highlight the current delivery
   * address (the selected account address when signed in — the signed-out
   * popover's single location-derived row is highlighted statically in the
   * markup), refresh the location-derived address line (signed out), clear a
   * stale geolocation error, and position the panel under the pill.
   *
   * @param {Event} event
   */
  #handleDeliveryToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState !== 'open') return;

    this.#hideModeTip();

    const { deliveryError, deliveryPopover, shippingButton } = this.refs;
    if (deliveryError) deliveryError.hidden = true;

    this.#reflectDeliverySelection();
    this.#refreshCurrentLocationAddress();
    this.#positionModePopover(deliveryPopover, shippingButton);
  };

  /** The mode popovers' CSS width cap — keep in step with
   *  .map-search__mode-popover in map-search.css. */
  static #POPOVER_MAX_WIDTH = 340;

  /**
   * Positions a mode popover below the bar, its right edge aligned to the
   * pill, clamped into the viewport. Fixed viewport coordinates are correct
   * in every placement — the popover renders in the top layer, so no ancestor
   * transform, containment or overflow shifts or clips it (the same reason
   * the mobile mode tooltip is a body portal). Runs from `beforetoggle`
   * while the popover is still display:none and unmeasurable, so the width
   * is mirrored from its (deterministic) CSS instead of being measured.
   *
   * @param {HTMLElement | undefined} popover
   * @param {HTMLElement} anchor - The pill the popover's right edge aligns to.
   */
  #positionModePopover(popover, anchor) {
    if (!popover) return;

    const margin = 16;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(MapSearchComponent.#POPOVER_MAX_WIDTH, window.innerWidth - 2 * margin);
    const left = Math.max(margin, Math.min(rect.right - width, window.innerWidth - width - margin));
    popover.style.left = `${left}px`;
    // Sit the same 8px below the bar as the fallback results dropdown
    // (.map-search__dropdown: top calc(100% + 8px)). Measure from the bar's
    // bottom, not the pill's — the pill is inset by the bar's block padding,
    // so anchoring to it would leave a visibly smaller gap.
    const bar = this.querySelector('.map-search__bar');
    const barBottom = bar ? bar.getBoundingClientRect().bottom : rect.bottom;
    popover.style.top = `${barBottom + 8}px`;
  }

  /** Closes both mode popovers (scroll/resize — a fixed panel can't follow
   *  its pill, so it is dismissed rather than left floating adrift). */
  #closeModePopovers = () => {
    for (const popover of [this.refs.pickupPopover, this.refs.deliveryPopover]) {
      if (popover && this.#isPopoverOpen(popover)) popover.hidePopover();
    }
  };

  /**
   * Closes the mode popovers when the page scrolls — but not when the scroll
   * happens inside one of them (the shipping address list scrolls; scroll
   * events don't bubble, but a capture listener on document still sees them).
   *
   * @param {Event} event
   */
  #handleDocumentScroll = (event) => {
    const { target } = event;
    if (
      target instanceof Node &&
      (this.refs.pickupPopover?.contains(target) || this.refs.deliveryPopover?.contains(target))
    ) {
      return;
    }
    this.#closeModePopovers();
  };

  /**
   * Fills the popovers' location-derived address lines — the Pickup
   * popover's "current location" line and, signed out, the Shipping
   * popover's shipping-address line (both describe the same stored point) —
   * with the closest address, reverse-geocoded via Mapbox in the storefront
   * language. Cached per coordinate, so reopening a popover at an unchanged
   * location costs no request. The rounded coordinates show while the
   * geocoder runs and stay as the fallback when it can't do better (no
   * token, failure, or nothing there) — still a truthful representation of
   * the stored point. With no stored location at all, the "not set" hint
   * shows instead.
   */
  async #refreshCurrentLocationAddress() {
    const targets = [this.refs.pickupAddress, this.refs.deliveryAddress].filter(Boolean);
    if (targets.length === 0) return;
    const strings = this.refs.pickupPopover ?? this.refs.deliveryPopover;

    const setText = (/** @type {string} */ text) => {
      for (const target of targets) target.textContent = text;
    };

    const location = this.#location;
    if (!location) {
      setText(strings?.dataset.i18nUnknown ?? 'No location set');
      return;
    }

    const key = `${location.lat.toFixed(5)},${location.lng.toFixed(5)}`;
    if (this.#resolvedAddress?.key === key) {
      setText(this.#resolvedAddress.text);
      return;
    }

    setText(`${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
    if (!this.#mapboxToken) return;

    // Supersede any older resolution still in flight.
    this.#addressController?.abort();
    this.#addressController = new AbortController();
    const { signal } = this.#addressController;

    const text = await resolveNearestAddress({
      lat: location.lat,
      lng: location.lng,
      token: this.#mapboxToken,
      language: this.dataset.locale,
      signal,
    });
    if (signal.aborted || !text) return;

    this.#resolvedAddress = { key, text };
    // Render only if the location hasn't moved on while resolving.
    const current = this.#location;
    if (current && `${current.lat.toFixed(5)},${current.lng.toFixed(5)}` === key) {
      setText(text);
    }
  }

  /**
   * The popovers' "Use my exact location" action (the Pickup popover, and the
   * signed-out Shipping popover): reads the browser's precise geolocation —
   * the one deliberate permission prompt in the flow (the page-load
   * resolution never prompts, see #resolveLocation) — and makes it the new
   * current location, exactly like the map's geolocate control (source
   * `geolocation`, explicit). The open popover stays open and its address
   * line re-resolves, so the visitor sees where search now thinks they are —
   * and, signed out in Shipping, the province the zone derives from follows
   * via #resolveShipsTo. On failure (denied, unavailable, timeout) the error
   * notice shows and the previous address line is restored.
   */
  usePreciseLocation = () => {
    if (this.#locatePending) return;

    const { pickupError, deliveryError, pickupLocateButton, deliveryLocateButton } = this.refs;
    const errors = [pickupError, deliveryError].filter(Boolean);
    const buttons = [pickupLocateButton, deliveryLocateButton].filter(Boolean);
    const strings = this.refs.pickupPopover ?? this.refs.deliveryPopover;
    for (const error of errors) error.hidden = true;

    const showErrors = () => {
      for (const error of errors) error.hidden = false;
    };

    if (!navigator.geolocation) {
      showErrors();
      return;
    }

    this.#locatePending = true;
    for (const button of buttons) {
      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');
    }
    const locating = strings?.dataset.i18nLocating ?? 'Determining your location…';
    for (const target of [this.refs.pickupAddress, this.refs.deliveryAddress]) {
      if (target) target.textContent = locating;
    }

    const finish = () => {
      this.#locatePending = false;
      for (const button of buttons) {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (this.#controller.signal.aborted) return;
        finish();
        this.#setLocation(position.coords.latitude, position.coords.longitude, 'geolocation', { explicit: true });
        // #setLocation skips an unchanged coordinate — refresh regardless so
        // the "locating…" line always resolves back to the address.
        this.#refreshCurrentLocationAddress();
      },
      () => {
        if (this.#controller.signal.aborted) return;
        finish();
        showErrors();
        this.#refreshCurrentLocationAddress();
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };

  /**
   * Selects an account address as the current shipping address (a row in the
   * signed-in Shipping popover). Its ISO 3166-2 zone (data-ships-to, from the
   * address's country + province) becomes the `ships_to` filter and owns it —
   * the automatic location-based resolution stands down — and the choice is
   * persisted and highlighted. Closes the popover: the selection is done, and
   * a Shipping-mode search refreshes right behind it.
   *
   * @param {Event} event
   */
  selectDeliveryAddress = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-address-id]') : null;
    if (!(button instanceof HTMLElement)) return;

    const zone = (button.dataset.shipsTo || '').toUpperCase();
    if (!zone) return;

    this.#delivery = { source: 'account', id: button.dataset.addressId ?? '', shipsTo: zone };
    this.#reflectDeliverySelection();

    if (this.#shipsTo !== zone) {
      this.#shipsTo = zone;
      if (this.#mode === 'shipping' && this.query) this.#runSearch();
    }
    // The address names the shipping region too (its province, or its
    // country) — the "Ships to {region}" lines follow the selection
    // instantly, no geocoder round-trip. The zone rides along so the map can
    // tell this apart from a same-zone name resolution: its current cards
    // were filtered by the OLD zone, and relabeling them with the new region
    // would claim shops ship somewhere they were never checked against.
    this.#setShipsToRegion(button.dataset.region ?? '', zone);
    this.#persist();

    const { deliveryPopover } = this.refs;
    if (deliveryPopover && this.#isPopoverOpen(deliveryPopover)) deliveryPopover.hidePopover();
  };

  /** Highlights the selected account address in the Shipping popover
   *  (`is-selected` + aria-pressed). No selection, no highlight — the zone
   *  is then the automatic location-derived default. */
  #reflectDeliverySelection() {
    const selectedId = this.#delivery?.id ?? null;
    for (const button of this.refs.deliveryAddressButtons ?? []) {
      const selected = selectedId != null && button.dataset.addressId === selectedId;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    }
  }

  /**
   * Restores a persisted account shipping address — only while signed in
   * (data-logged-in) AND the address still matches a rendered account-address
   * row: a deleted address self-heals to the automatic zone, and an edited
   * one re-derives its zone from the fresh markup rather than trusting the
   * persisted copy. Signed out, any stale selection is dropped (the next
   * #persist clears it) and the plain persisted-zone restore above stands.
   *
   * @param {unknown} value - The persisted `delivery` payload.
   */
  #restoreDelivery(value) {
    if (!this.hasAttribute('data-logged-in')) return;
    if (value == null || typeof value !== 'object') return;

    const { source, id } = /** @type {any} */ (value);
    if (source !== 'account' || id == null) return;

    const match = (this.refs.deliveryAddressButtons ?? []).find(
      (button) => button.dataset.addressId === String(id)
    );
    const zone = (match?.dataset.shipsTo || '').toUpperCase();
    if (!zone) return;

    this.#delivery = { source: 'account', id: String(id), shipsTo: zone };
    this.#shipsTo = zone;
    // The region re-derives from the fresh markup like the zone, so an edited
    // address never keeps a stale persisted region.
    this.#shipsToRegion = match?.dataset.region ?? '';
    this.#reflectDeliverySelection();
  }

  /**
   * Removes the context-collection chip and drops the `coll` filter, then
   * re-runs the search so results widen beyond the collection.
   *
   * @param {Event} event
   */
  removeChip = (event) => {
    // Locked in the demo-scoped state: a valid-token demo visitor may not widen
    // the search past their own shop (data-demo-scope, see map-search-bar.liquid).
    // The button is omitted server-side; this guards any other caller so the
    // context filter can never be dropped there.
    if (this.dataset.demoScope != null) return;
    const { target } = event;
    if (target instanceof Element) target.closest('.map-search__chip')?.remove();
    this.#collection = null;
    if (this.query) {
      this.#runSearch();
    } else {
      // No search ongoing: dropping the collection scope widens the idle pins
      // (no collection → every pin shows).
      this.#refreshIdlePins();
    }
  };

  /**
   * Reflects the active mode on the two mode buttons (active class + aria).
   */
  #reflectMode() {
    const { locationButton, shippingButton } = this.refs;
    const isLocation = this.#mode === 'location';

    locationButton.classList.toggle('is-active', isLocation);
    locationButton.setAttribute('aria-pressed', String(isLocation));
    shippingButton.classList.toggle('is-active', !isLocation);
    shippingButton.setAttribute('aria-pressed', String(!isLocation));
  }

  /**
   * Updates the active search mode, reflects + persists it, and re-runs the
   * search (the mode changes which parameters are sent).
   *
   * @param {'location' | 'shipping'} mode
   */
  #setMode(mode) {
    if (this.#mode !== mode) {
      this.#mode = mode;
      this.#reflectMode();
      this.#persist();
    }

    if (this.query) this.#runSearch();
  }

  /**
   * Caches the map's visible viewport and, in location mode with an active
   * query, refreshes results so search follows the map as it is panned. Only
   * the bounding box changes on a pan, so the refreshed results ADD to the
   * cached set (see #runSearch).
   *
   * @param {Event} event
   */
  #handleBoundsChanged = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const { north, south, east, west } = event.detail ?? {};
    if (![north, south, east, west].every((value) => typeof value === 'number' && Number.isFinite(value))) {
      return;
    }

    this.#bounds = { north, south, east, west };
    // Search follows the map only when results render there — the dropdown's
    // location-scoped results don't change with the map viewport.
    if (this.#mode === 'location' && this.query && this.#useMapResults) this.#refreshOnPan();
    // With no search ongoing the bar drives the idle pins instead: on a
    // collection page only the locations carrying a product of the collection
    // should stay pinned as the map moves (mode-independent — there is no
    // search to be moded).
    if (!this.query && this.#useMapResults) {
      // The initial view's listing goes out at once (no listing has run
      // yet); later pans are debounced.
      if (this.#idleController) this.#refreshIdlePinsOnPan();
      else this.#refreshIdlePins();
    }
  };

  /**
   * Applies an explicit current-location change from the map: the geolocate
   * control (`source: 'geolocation'`) or the right-click "Set as current
   * location" (`source: 'manual'`). Explicit — the visitor just did it — so
   * it always wins over the current location.
   *
   * @param {Event} event
   */
  #handleLocationChanged = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const { lat, lng, source } = event.detail ?? {};
    // Finite-only (not just typeof): a NaN/Infinity coordinate would persist
    // and poison every subsequent search with a 400.
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    this.#setLocation(lat, lng, source === 'manual' ? 'manual' : 'geolocation', { explicit: true });
  };

  /** Debounced re-search when the map is panned (location mode). Tagged 'pan'
   *  so the map updates its results without recentering (see mapbox.js). */
  #refreshOnPan = debounce(() => this.#runSearch('pan'), 400);

  /**
   * Updates the current location. An explicit update (the visitor just used
   * the geolocate control or "Set as current location") always applies; an
   * automatic one (the page-load resolution) never downgrades to a
   * less-authoritative source — see LOCATION_SOURCE_RANK. The location is part
   * of the search context, so a change empties the cached results and
   * re-runs the search.
   *
   * @param {number} lat
   * @param {number} lng
   * @param {'manual' | 'geolocation' | 'ip'} source
   * @param {{ explicit?: boolean }} [options]
   */
  #setLocation(lat, lng, source, { explicit = false } = {}) {
    const current = this.#location;
    if (!explicit && current && LOCATION_SOURCE_RANK[current.source] > LOCATION_SOURCE_RANK[source]) return;
    if (current && current.lat === lat && current.lng === lng && current.source === source) return;

    // `at` lets shopla-current-location.js reuse an automatic fix on later
    // pages without resolving again.
    this.#location = { lat, lng, source, at: Date.now() };
    this.#persist();
    // Keep the province default in step with the new location.
    this.#resolveShipsTo();
    // Keep an open popover honest about the location it describes.
    if (this.#anyModePopoverOpen()) this.#refreshCurrentLocationAddress();
    if (this.query) this.#runSearch();
  }

  /**
   * Resolves the visitor's current location on load without ever prompting —
   * the shared resolution in shopla-current-location.js (remembered location,
   * else granted geolocation, else GeoIP), which the map on the page awaits
   * for its initial center too, so it happens once. A persisted manual choice
   * is sticky. Explicit actions (the geolocate control, the map's "Set as
   * current location") update it later via `map:location-changed`.
   */
  async #resolveLocation() {
    if (this.#location?.source === 'manual') return;

    const location = await resolveCurrentLocation({ searchUrl: this.dataset.searchUrl });
    if (this.#controller.signal.aborted || !location) return;
    this.#setLocation(location.lat, location.lng, location.source);
  }

  /** Whether a ships_to code is the selected country or one of its provinces. */
  #belongsToCountry(code) {
    return !!this.#country && (code === this.#country || code.startsWith(`${this.#country}-`));
  }

  /** Debounced province resolution from the current location (Mapbox lookup). */
  #resolveShipsTo = debounce(() => this.#doResolveShipsTo(), 500);

  /**
   * Upgrades #shipsTo from the selected country to the visitor's ISO 3166-2
   * province via the Mapbox geocoder — the same lookup also carries the
   * region's display name for the "Ships to {region}" lines — keyed on the
   * current location so a re-resolution from the same rounded coordinate
   * doesn't re-query. Best-effort:
   *   - aborts any older in-flight resolution so a slow, stale geocode can't
   *     clobber a fresher location;
   *   - caches the (country|lat|lng) key only on a DETERMINISTIC result, so a
   *     transient geocoder failure (resolver → null) is retried on the next
   *     location change rather than pinning the spot to country-level forever;
   *   - re-runs the search only when the zone actually changes while the visitor
   *     is shipping-searching. A name-only change never refetches — the name
   *     is display-only, so #setShipsToRegion just tells the renderers.
   */
  async #doResolveShipsTo() {
    // An explicitly selected account shipping address owns the zone — the
    // automatic location-based default stands down until it is cleared.
    if (this.#delivery) return;
    if (!this.#mapboxToken || !this.#country || !this.#location) return;

    const { lat, lng } = this.#location;
    // The province only changes when the rounded coordinate (or country) does.
    const key = `${this.#country}|${lat.toFixed(2)}|${lng.toFixed(2)}`;
    if (key === this.#resolvedKey) return;

    // Supersede any older resolution still in flight.
    this.#resolveController?.abort();
    this.#resolveController = new AbortController();
    const { signal } = this.#resolveController;

    const region = await resolveShippingRegion({
      lat,
      lng,
      countryCode: this.#country,
      token: this.#mapboxToken,
      language: this.dataset.locale,
      signal,
    });
    // Superseded by a newer resolution / disconnect, or a transient failure
    // (null) — leave the key uncommitted so a later move retries.
    if (signal.aborted || region == null) return;

    // Deterministic result (province or country fallback): cache the spot.
    // The display name lands first — it can change even when the zone code
    // stays (e.g. a first resolution of the persisted country default).
    this.#setShipsToRegion(region.name, region.code);
    this.#resolvedKey = key;
    // Persisted even when the zone is unchanged: the key is what lets the
    // next page skip this request.
    this.#persist();
    if (region.code === this.#shipsTo) return;

    this.#shipsTo = region.code;
    this.#persist();
    // Refresh only if the visitor is actively shipping-searching.
    if (this.#mode === 'shipping' && this.query) this.#runSearch();
  }

  /**
   * Applies a new shipping-region display name: persists it and broadcasts
   * `shopla:ships-to:changed` so every "Ships to {region}" renderer — the
   * map's delivery-mode cards, a product page's ships-to block — updates in
   * place. Display-only, so it never re-runs the search. A no-op when
   * unchanged.
   *
   * The event carries the ZONE the name describes alongside it: the map only
   * applies the event to cards whose result set was filtered by that same
   * zone (a late name resolution); when the zone changed too, its cards are
   * stale and keep their old label until the refreshed results replace cards
   * and label together. A product page's ships-to block has no result set to
   * contradict, so it applies every event immediately.
   *
   * @param {string} region - The region name, or '' when none could be resolved.
   * @param {string} zone - The ships_to zone code the name belongs to.
   */
  #setShipsToRegion(region, zone) {
    if (region === this.#shipsToRegion) return;
    this.#shipsToRegion = region;
    this.#persist();
    this.#emit('shopla:ships-to:changed', { region, zone });
  }

  /**
   * Hides the fallback dropdown when a click lands outside the search box.
   *
   * @param {Event} event
   */
  #handleDocumentClick = (event) => {
    const { results } = this.refs;
    if (!results || results.hidden) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    results.hidden = true;
  };

  /**
   * Identity of the current search context — everything that shapes the
   * result set EXCEPT the bounding box: query, mode, filters (shipping zone /
   * collection) and the current location. While this stays constant, panning
   * the map only ADDS results (the box is deliberately excluded); when it
   * changes, the cached results are emptied.
   *
   * @returns {string}
   */
  #searchContextKey() {
    return JSON.stringify({
      q: this.query,
      mode: this.#mode,
      l: this.#location ? `${this.#location.lat},${this.#location.lng}` : '',
      shipsTo: this.#mode === 'shipping' ? this.#shipsTo : '',
      coll: this.#collection ?? '',
    });
  }

  /**
   * Runs the full search: query the shopla-search service for ranked product
   * ids, then render those products via Shopify's predictive-search section.
   *
   * @param {'query' | 'pan'} [reason] - What triggered this run. 'pan' (a map
   *   move) tells the map to update results without recentering; 'query' (a
   *   keystroke / mode / chip change) lets it recenter if needed.
   */
  async #runSearch(reason = 'query') {
    const query = this.query;
    if (!query) {
      // Clearing the box supersedes any in-flight search / carousel load — a
      // late response must not repopulate the just-cleared results.
      this.#searchController?.abort();
      this.#locationController?.abort();
      this.#clear();
      // The search no longer owns the pins — restore the idle set (all pins,
      // or the collection-scoped listing on a collection page).
      this.#refreshIdlePins();
      return;
    }

    // Demo storefront (staging): the bar looks and behaves exactly like
    // production until a query is entered, then — instead of running a
    // cross-store search that would surface other demo merchants' products —
    // it shows a "try it live" notice in the results area (the map's results
    // pane, or the fallback dropdown with no map) and makes NO backend request.
    // Armed by the data-demo-* attributes from map-search-bar.liquid; kept in
    // sync with the shopla-search app extension. Placed after the empty-query
    // guard, so an empty bar still clears and restores the idle pins exactly
    // like production.
    if (this.dataset.demoDisabled != null) {
      this.#searchController?.abort();
      this.#showDemoNotice();
      return;
    }

    // What "nothing matched" means depends on what bounded the search: a
    // pickup search is bounded by the map's viewport, so the way out is to
    // zoom out; a delivery search is bounded by the visitor's address, and
    // panning the map cannot widen it. The fallback dropdown has no map to
    // zoom, so it keeps the plain "no products" wording.
    const emptyText = this.#useMapResults ? this.#emptyText() : this.dataset.i18nEmpty ?? 'No products found';

    // Cancel any in-flight request so only the latest query renders.
    this.#searchController?.abort();
    this.#searchController = new AbortController();
    const { signal } = this.#searchController;

    // Empty the cached map results when the search context changed — any
    // change to q, mode, filters or location starts fresh, and supersedes any
    // per-location carousel load in flight (its response belongs to the old
    // context and must not be cached into the new one). The map then holds
    // that carousel as it is — spinner up — until this search's outcome
    // settles it (results refresh it; an empty or failed search closes it),
    // so every path out of here must end in one of those events. Only a
    // bounding-box-only change (a pan/zoom) keeps and adds to the cache — and
    // deliberately leaves a carousel load alone: panning must not blank an
    // open carousel that is still fetching.
    const contextKey = this.#searchContextKey();
    // Captured before the branch below mutates it: whether this search starts a
    // FRESH context (q, mode, filters or location changed) rather than merely
    // re-running the same one over a new viewport. The carousel warm-up further
    // down turns on it — see there for why a pan must not do it.
    const freshContext = contextKey !== this.#contextKey;
    if (contextKey !== this.#contextKey) {
      this.#contextKey = contextKey;
      this.#locationController?.abort();
      this.#locationCache.clear();
      this.#fetchedBoxes.clear();
      this.#deliveryEntries.clear();
      this.#deliveryPages.clear();
    }

    this.#status('loading', this.dataset.i18nLoading ?? 'Searching…');

    try {
      const entries = await this.#fetchResults(signal);
      if (signal.aborted) return;

      // `null` means the search couldn't be formed (no l / bb / ships_to /
      // coll to constrain by) — a distinct state from a query that genuinely
      // matched nothing, so the visitor gets an actionable hint instead of
      // "no results".
      if (entries === null) {
        this.#status('empty', this.dataset.i18nNoLocation ?? 'Open the map or switch to Shipping to search');
        return;
      }

      if (this.#useMapResults) {
        // Merge the located hits into the bounded per-context cache — keyed by
        // shop location, first appearance (of its closest-ranked product) wins
        // — so panning adds results to the map instead of replacing them, then
        // hand the ordered shop locations of the WHOLE cache to the map, which
        // renders a card per location and drives the in-map product carousels
        // (fetched lazily via `loadLocationProducts`, which caches pages).
        //
        // A blank handle is a delivery-only hit. Those used to be dropped here,
        // which made a mail-order product invisible on any page with a map (and
        // an all-mail-order result set report that nothing matched). They are
        // now kept aside for the map's "Shipping only" card. Collected in
        // "Shipping" mode only: a location search filters location-less docs
        // out server-side, so a blank handle there would be a stray, not a
        // mail-order product.
        // Delivery-only hits are grouped by SHOP — one card per shop, branded
        // from its shop collection like a shop-location card.
        //
        // Each shop's card ranks among the location cards at the position of
        // its best product: the number of DISTINCT locations whose best product
        // outranks it. Location cards are ordered by first appearance in the
        // ranked results, so counting the distinct location handles seen before
        // a shop's first delivery-only hit places its card at exactly its own
        // rank in that same order.
        const delivery = new Map();
        const deliveryRanks = new Map();
        const rankedAhead = new Set();
        for (const entry of entries) {
          if (entry.handle) {
            this.#touchLocation(entry.handle, entry.distance);
            rankedAhead.add(entry.handle);
          } else if (this.#mode === 'shipping') {
            let bucket = delivery.get(entry.shop);
            if (!bucket) {
              bucket = [];
              delivery.set(entry.shop, bucket);
              // First hit for this shop — its rank is however many distinct
              // locations have already appeared above it.
              deliveryRanks.set(entry.shop, rankedAhead.size);
            }
            bucket.push(entry);
          }
        }
        // Replaced, not merged: delivery results don't depend on the viewport,
        // so a pan can't add to them. Their page cache is emptied alongside the
        // other per-context caches (above) on the only thing that can change
        // them — a change of search context, which #searchContextKey keys on
        // every input the delivery result set depends on (q, mode, ships-to,
        // collection, location).
        this.#deliveryEntries = delivery;

        const locations = [...this.#locationCache.values()].map(({ handle, distance }) => ({ handle, distance }));
        if (locations.length === 0 && delivery.size === 0) {
          this.#noResults(emptyText);
          return;
        }

        // Warm the carousel the map is about to open, ALONGSIDE the place
        // lookup below instead of after it.
        //
        // The map opens the first matched location's carousel (mapbox.js
        // #bestLocatedHandle), and that load — a `coll`-scoped /search, ~0.9s
        // against the live service — needs nothing but the handle, which is
        // known right here. It used to start only once the results event fired,
        // i.e. queued behind the ~0.5s place lookup, for no reason at all.
        // Starting it now means the map's own call finds the page already in
        // `#locationCache` and issues no request (loadLocationProducts checks
        // the cache before it touches the abort controller).
        //
        // FRESH CONTEXTS ONLY. A pan re-emits the same results with a carousel
        // already open, and deliberately does not abort its in-flight load;
        // starting one here would abort it — the bar runs one carousel load at
        // a time — and blank a carousel that was doing fine.
        //
        // LOCATION MODE ONLY. In shipping mode the map opens the first IN-VIEW
        // location, or a delivery carousel instead, neither of which is
        // knowable from here.
        //
        // Best-effort throughout: if the map opens a different location the
        // page is simply cached for when the visitor clicks that card, and a
        // rejection — superseded by the map's own load, or a genuine failure —
        // is that load's to surface, not this one's. Hence the bare catch: this
        // is a cache warm-up, and its only job is not to become an unhandled
        // rejection.
        if (freshContext && this.#mode !== 'shipping' && locations.length > 0) {
          Promise.resolve(this.loadLocationProducts(locations[0].handle)).catch(() => {});
        }
        // `query` lets the map tell a refinement (keystroke → refresh products)
        // apart from a same-query re-emit (map pan → keep the open carousel);
        // `reason` tells a pan (never recenter, never open a carousel) from a
        // query change (may do both); `mode` tells it whether the results are
        // viewport-filtered (location) and should follow the map as it moves;
        // `delivery` is one entry per shop with delivery-only matches — its
        // handle, its rank among the location cards, and the shop's branding so
        // the map can build the card synchronously. The products themselves are
        // pulled lazily through `loadDeliveryProducts`. `places` carries the
        // matched shop locations the map may not have a pin for yet, in the
        // shape it renders pins and cards from.
        //
        // Resolved before the emit rather than after: the map builds its cards
        // the moment the event lands, so branding that arrives later would mean
        // a second event and a re-render.
        //
        // ONE lookup for all of it — the matched locations, the shops behind
        // them, and the delivery-only shops. These used to be three sequential
        // round trips (locations, then the shops of whichever locations turned
        // out to lack a logo, then the delivery shops) at ~0.5s each against
        // the live service. They never needed to be sequential: every hit
        // already carries its `shop_handle`, so nothing has to come back before
        // the rest can be asked for, and a place lookup can name locations and
        // shops in the same request (see #resolvePlaces).
        //
        // Matched locations the map has no pin for are dropped by it, so the
        // ones it hasn't seen are resolved here and travel with the results.
        // Without this a location the page never server-rendered — anything
        // past the old 128-entry payload, or simply outside the page's own
        // collection — could match a search and still be invisible.
        //
        // Never blocks the result — on failure the map falls back to its
        // unbranded card.
        const places = await this.#resolveLocations(
          locations.map(({ handle }) => handle),
          signal,
          entries.map((entry) => entry.shop)
        );
        if (signal.aborted) return;

        this.#emit('shopla:search:results', {
          locations,
          places,
          delivery: [...delivery.keys()].map((handle) => ({
            handle,
            rank: deliveryRanks.get(handle) ?? 0,
            shop: this.#places.get(handle) ?? null,
          })),
          query,
          reason,
          mode: this.#mode,
          // The shipping region's display name the delivery-mode cards'
          // "Ships to {region}" lines show ('' while unresolved — the cards
          // then omit the line; a later resolution reaches the map via
          // `shopla:ships-to:changed`), and the zone it belongs to — what the
          // map checks ships-to-changed events against, so a zone switch can
          // never relabel cards that were filtered by the old zone.
          shipsToRegion: this.#shipsToRegion,
          shipsToZone: this.#shipsTo,
          // The map's pane can fall empty later in the same search — a pan
          // that takes the last matched pin off screen — so it needs the copy
          // for that state even on a results event that isn't empty.
          emptyText,
          register: this.#registerPrompt(),
        });
        return;
      }

      if (entries.length === 0) {
        this.#noResults(emptyText);
        return;
      }

      // No map: render the product cards directly in the fallback dropdown.
      await this.#renderProducts(entries, signal);
    } catch (error) {
      if (signal.aborted) return;
      console.warn('Map search failed:', error);
      this.#status('error', this.dataset.i18nError ?? 'Search is unavailable right now');
    }
  }

  /**
   * Returns the cache bucket for a shop location, creating it on first sight
   * and stamping its recency; beyond MAX_CACHED_LOCATIONS the
   * least-recently-seen bucket (and its cached products) is evicted. The
   * distance keeps its first-seen value — that of the location's first
   * (closest-ranked) matching product, which every product at the location
   * shares; it derives from the current location, which is fixed for the
   * lifetime of the cache. `null` off location searches.
   *
   * @param {string} handle
   * @param {number | null} distance
   * @returns {{ handle: string, distance: number | null, lastSeen: number, pages: Map<string, { products: { id: string, html: string, distance: number | null }[], hasMore: boolean }> }}
   */
  #touchLocation(handle, distance) {
    let bucket = this.#locationCache.get(handle);
    if (!bucket) {
      bucket = { handle, distance, lastSeen: 0, pages: new Map() };
      this.#locationCache.set(handle, bucket);
    } else if (bucket.distance == null && distance != null) {
      bucket.distance = distance;
    }
    bucket.lastSeen = ++this.#cacheSeq;

    while (this.#locationCache.size > MAX_CACHED_LOCATIONS) {
      let oldest = null;
      for (const candidate of this.#locationCache.values()) {
        if (!oldest || candidate.lastSeen < oldest.lastSeen) oldest = candidate;
      }
      this.#locationCache.delete(oldest.handle);
    }
    return bucket;
  }

  /**
   * Queries the shopla-search service for the global result set and returns
   * ranked entries.
   *
   * Returns `null` when no constraint (l / bb / ships_to / coll) could be
   * formed — the caller renders an actionable hint rather than an
   * empty-results message.
   *
   * @param {AbortSignal} signal
   * @returns {Promise<{ id: string, handle: string, distance: number | null }[] | null>}
   */
  async #fetchResults(signal) {
    // Missing service config → treat as an empty (not "no location") result.
    if (!this.dataset.searchUrl) return [];

    // When results go to the map's pane, ask for more products than the
    // service default so enough distinct shop locations surface to populate it.
    const url = this.#buildSearchUrl({ size: this.#useMapResults ? GLOBAL_SIZE : undefined });
    if (url === null) return null;

    // An identical box was already fetched for this (unchanged) context —
    // serve the cached results without another request (e.g. a re-emit
    // of the same bounds, or panning back and forth).
    const bb = url.searchParams.get('bb');
    if (bb && this.#fetchedBoxes.has(bb)) return [];

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`shopla-search responded ${response.status}`);

    const entries = this.#parseResults(await response.json());
    if (bb) this.#fetchedBoxes.add(bb);
    return entries;
  }

  /**
   * Builds a `/search` request URL from the current query and mode. Shared by
   * the global search and per-location carousel loads.
   *
   * @param {{ coll?: string, from?: number, size?: number }} [options]
   *   - `coll`: narrow to a single shop-location handle (a carousel load),
   *     overriding the page's context collection.
   *   - `from` / `size`: pagination for a location's carousel.
   * @returns {URL | null} `null` when the service is unconfigured or no
   *   constraint (l / ships_to / coll) could be formed.
   */
  #buildSearchUrl({ coll, from, size } = {}) {
    const base = this.dataset.searchUrl;
    if (!base) return null;

    const url = new URL(`${base.replace(/\/+$/, '')}/search`);
    url.searchParams.set('q', this.query);

    // The shop whose per-instance index to search (`shopla-products-<s>`). The
    // service requires it; `shop.permanent_domain` is the canonical
    // <shop>.myshopify.com (see data-shop in map-search-bar.liquid).
    const shop = this.dataset.shop;
    if (shop) url.searchParams.set('s', shop);

    // The storefront's active language (e.g. "de", "fr"), so the service can
    // bridge a query in the buyer's language to a mixed-language catalogue via
    // locale-aware query expansion. Optional: the service defaults to no
    // expansion when it is absent (see data-locale in map-search-bar.liquid).
    const locale = this.dataset.locale;
    if (locale) url.searchParams.set('locale', locale);

    if (this.#mode === 'shipping') {
      // "Shipping" filters by the shipping zone ALONE — deliberately without
      // `l`. A buyer location makes the service add an `exists: location`
      // filter, which drops every mail-order product (they are indexed with no
      // geo point) — precisely the products a delivery search is for. So
      // results carry no `_distance` in this mode, with or without a map.
      if (this.#shipsTo) url.searchParams.set('ships_to', this.#shipsTo);
    } else {
      // "Pickup" sends the current location (geolocation / GeoIP / a manual
      // choice — never a map center): it restricts results to products with a
      // physical location and makes them carry `_distance` (which never ranks
      // them). With a map on the page it additionally FILTERS to the map's
      // expanded viewport box. That box discovers results across the map, so
      // it only applies when results render there — not to a carousel load
      // (already scoped to one shop location; panning away must not empty an
      // open carousel) or a location page's dropdown (scoped by its
      // collection).
      if (this.#useMapResults && !coll) {
        const box = this.#expandedBounds();
        // Until the map reports its viewport there is nothing to filter to —
        // treat the search as unformable (the first bounds event re-runs it)
        // rather than letting geographically unfiltered results pollute the
        // additive cache.
        if (!box) return null;
        url.searchParams.set('bb', box);
      }
      if (this.#location) url.searchParams.set('l', `${this.#location.lat},${this.#location.lng}`);
    }

    // A per-location carousel load narrows to that single shop-location handle
    // (its products are a subset of any context collection, so it can stand
    // alone); otherwise the page's context collection (if any) applies.
    const collection = coll ?? this.#collection;
    if (collection) url.searchParams.set('coll', collection);

    if (typeof from === 'number') url.searchParams.set('from', String(from));
    if (typeof size === 'number') url.searchParams.set('size', String(size));

    // The service needs at least one of l / bb / ships_to / coll; with none
    // there is nothing to constrain the cross-shop search. Signal that
    // distinctly (null) so the caller can prompt the visitor instead of
    // showing "no results".
    if (
      !url.searchParams.has('l') &&
      !url.searchParams.has('bb') &&
      !url.searchParams.has('ships_to') &&
      !url.searchParams.has('coll')
    ) {
      return null;
    }

    return url;
  }

  /**
   * The `bb` filter value for the current viewport: the visible bounds
   * expanded around their center so the box covers (at least) twice the
   * visible area — each axis is scaled by √2, doubling the surface — then
   * clamped to valid latitudes and wrapped to valid longitudes. `null` while
   * the map hasn't reported bounds yet.
   *
   * @returns {string | null} `"top,left,bottom,right"` (lat,lon,lat,lon)
   */
  #expandedBounds() {
    const bounds = this.#bounds;
    if (!bounds) return null;

    // Padding added to each side: half the extra span of a √2× axis scale.
    const pad = (Math.SQRT2 - 1) / 2;
    const latPad = (bounds.north - bounds.south) * pad;
    const lngPad = (bounds.east - bounds.west) * pad;

    // Latitude clamps at the poles (the map view already covers everything
    // beyond them).
    const top = Math.min(90, bounds.north + latPad);
    const bottom = Math.max(-90, bounds.south - latPad);

    let left = bounds.west - lngPad;
    let right = bounds.east + lngPad;
    if (right - left >= 360) {
      // The expanded box wraps the whole globe — send the full range.
      left = -180;
      right = 180;
    } else {
      // Wrap into [-180, 180); left > right is the antimeridian-crossing form
      // the service (and Elasticsearch) resolve natively.
      left = wrapLongitude(left);
      right = wrapLongitude(right);
    }

    const fixed = (value) => value.toFixed(6);
    return `${fixed(top)},${fixed(left)},${fixed(bottom)},${fixed(right)}`;
  }

  /** Debounced idle-pins refresh as the map is panned with no search ongoing. */
  #refreshIdlePinsOnPan = debounce(() => this.#refreshIdlePins(), 400);

  /**
   * Fetches places from the search service and caches every one it returns by
   * handle. The single request primitive behind both lookups below.
   *
   * Best-effort: any failure (unconfigured service, non-2xx, network error,
   * malformed body, or a superseded request) resolves to `null` and leaves the
   * cache untouched, so callers degrade to their unresolved fallback rather
   * than breaking. `null` rather than `[]` so a caller can tell a failure from
   * a genuinely empty result and not cache the former as a permanent miss.
   * Never throws, never rejects.
   *
   * @param {Record<string, string>} params - `/places` query params beside `s`.
   * @param {AbortSignal} signal
   * @returns {Promise<Record<string, any>[] | null>} the places returned, in order.
   */
  async #fetchPlaces(params, signal) {
    const base = this.dataset.searchUrl;
    const shop = this.dataset.shop;
    if (!base || !shop) return null;

    try {
      const url = new URL(`${base.replace(/\/+$/, '')}/places`);
      url.searchParams.set('s', shop);
      for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`shopla-search responded ${response.status}`);
      const body = await response.json();
      if (signal.aborted) return null;

      const places = [];
      for (const place of Array.isArray(body) ? body : []) {
        if (place && typeof place.handle === 'string' && place.handle) {
          this.#places.set(place.handle, place);
          places.push(place);
        }
      }
      return places;
    } catch (error) {
      if (signal.aborted) return null;
      console.warn('Places lookup failed:', error);
      return null;
    }
  }

  /**
   * Fills {@link #places} for the given handles — a shop's name, storefront
   * path, logo and cover, or a shop location's plus its coordinates, address
   * and opening hours.
   *
   * This replaced the `data-shops` and `data-locations` JSON blobs the map
   * sections used to render from Liquid, walked off the store-wide
   * `all-locations` collection. Shopify caps list metafields at 128 entries, so
   * that registry silently stopped growing — a store's 129th shop location was
   * unrenderable, and a merchant with no published location never appeared in
   * it at all, which is precisely the mail-order merchant whose delivery card
   * needs the branding. Looking places up by handle has no such ceiling and
   * costs one small request per place first seen.
   *
   * Only uncached handles are requested, and a handle the service returns
   * nothing for is cached as `null` so it isn't asked for again. A transient
   * failure is deliberately NOT cached as a miss, so it is retried.
   *
   * @param {string[]} handles - Of either kind; they share one namespace.
   * @param {AbortSignal} signal
   */
  async #resolvePlaces(handles, signal) {
    // A blank handle is a hit from a search service predating `shop_handle` —
    // there is nothing to look up, and it renders as one unbranded card.
    const missing = [...new Set(handles.filter((handle) => handle && !this.#places.has(handle)))];
    if (missing.length === 0) return;

    // No `type` filter. A place's handle is unique within a shop — they are all
    // Shopify collection handles — so one lookup can name LOCATIONS AND SHOPS
    // together, and the service answers with both. That is what lets a search
    // resolve a card's location and its shop's branding in one round trip
    // instead of two sequential ones (~0.5s each against the live service).
    //
    // The service caps one lookup at MAX_PLACE_HANDLES; chunk so a wide
    // viewport can't be rejected wholesale. The chunks don't depend on one
    // another, so they go together rather than one after the next.
    const chunks = [];
    for (let i = 0; i < missing.length; i += PLACE_LOOKUP_CHUNK) {
      chunks.push(missing.slice(i, i + PLACE_LOOKUP_CHUNK));
    }
    const answers = await Promise.all(
      chunks.map((chunk) => this.#fetchPlaces({ handle: chunk.join(',') }, signal))
    );
    if (signal.aborted) return;

    // Only a chunk that actually answered may record its misses — an unindexed
    // place must not be looked up again on every keystroke or pan, but a
    // transient failure must not unbrand it for the life of the page. Per
    // chunk, not all-or-nothing: one failed chunk should not cost the others
    // their misses.
    chunks.forEach((chunk, index) => {
      if (!answers[index]) return;
      for (const handle of chunk) {
        if (!this.#places.has(handle)) this.#places.set(handle, null);
      }
    });
  }

  /**
   * Resolves shop-location handles into the shape the map renders pins and
   * cards from (see `#addLocations` in mapbox.js), dropping any the service
   * doesn't know.
   *
   * A location with no cover image of its own falls back to its shop's, which
   * is why the parent shops are resolved too. That fallback lives here rather
   * than denormalised into the index, where a shop changing its image would
   * leave every one of its locations stale.
   *
   * @param {string[]} handles - Shop-location handles to resolve and return.
   * @param {AbortSignal} signal
   * @param {string[]} [shopHandles] - Parent-shop handles the caller already
   *   knows (every search hit carries one). Resolved in the SAME request as
   *   `handles`, so the cover/logo fallback costs no extra round trip. Omit
   *   them and it still works — the fallback below just runs a second lookup.
   * @returns {Promise<Record<string, any>[]>}
   */
  async #resolveLocations(handles, signal, shopHandles = []) {
    // One round trip for everything a card may need. `shopHandles` is what the
    // caller already knows — every search hit carries `shop_handle`, so a
    // search need not first fetch the locations to discover which of them lack
    // a logo, then fetch their shops. Resolving both together saves that whole
    // second round trip on the critical path.
    await this.#resolvePlaces([...handles, ...shopHandles], signal);
    if (signal.aborted) return [];

    const places = handles.map((handle) => this.#places.get(handle)).filter(Boolean);
    // A location normally carries its shop's logo and cover already (the index
    // duplicates them); the shop is only looked up for the ones that don't —
    // and only for shops the caller could NOT name up front, which is why this
    // resolves nothing at all on the search path. The idle-pin path still needs
    // it: a `/locations` listing names locations without their hits.
    await this.#resolvePlaces(
      places.filter((place) => !place.logo || !place.cover).map((place) => place.shop_handle),
      signal
    );
    if (signal.aborted) return [];

    return places.map((place) => {
      const shop = this.#places.get(place.shop_handle);
      return {
        handle: place.handle,
        title: place.title,
        url: place.url,
        // The index stores a geo point as `lat`/`lon`; the map speaks `lng`.
        lng: place.lon,
        lat: place.lat,
        logo: place.logo || shop?.logo || '',
        cover: place.cover || shop?.cover || '',
        address1: place.address1,
        zip: place.zip,
        city: place.city,
        country: place.country,
        // Renamed like `openingHours` below, and needed for the same line: the
        // card reads a location's hours on that location's clock, and this is
        // the fallback when the hours name no timezone of their own. `country`
        // above can't stand in — it is free display text.
        countryCode: place.country_code,
        openingHours: place.opening_hours,
      };
    });
  }

  /**
   * Drives the map's pins while NO search is ongoing. On a collection page
   * only the shop locations carrying at least one product of the collection
   * should be pinned, so the bar asks the search service's query-less
   * `/locations` listing for the locations inside the current (expanded)
   * viewport and hands the accumulated handles to the map
   * (`shopla:pins:changed`); everywhere else every pin shows
   * (`handles: null`) — the viewport itself is the bounding-box crop.
   *
   * Two handle sources, by whether the page has a context collection:
   *
   *   - WITH one (a collection page) the pins are product-scoped, so the
   *     handles come from `/locations` — the distinct locations carrying a
   *     product of that collection — and their presentation is looked up
   *     separately.
   *   - WITHOUT one (the homepage) every location in view shows, product or
   *     not. That set comes straight from `/places`, which indexes locations
   *     with their coordinates, so one request yields both the handles and
   *     what the map renders them from.
   *
   * The second case used to be served by a Liquid payload walking the
   * store-wide `all-locations` collection, whose list metafield Shopify caps at
   * 128 entries — so a store's 129th location simply had no pin. Listing the
   * viewport instead has no such ceiling.
   *
   * Panning accumulates like the search cache: an already-listed box is not
   * re-queried and fresh handles merge in; the accumulation belongs to the
   * context collection (removing the chip resets it). Fail-open: a service
   * failure shows every pin the map already has rather than none.
   */
  async #refreshIdlePins() {
    if (!this.#useMapResults || this.query) return;

    // A demo visitor whose token did not resolve to a shop gets NO pins rather
    // than the whole instance's. Without a context collection the listing below
    // is unscoped by design (that is what the homepage wants in production),
    // and handing a prospect every merchant's locations is a data leak. The bar
    // is already refusing to search in this state; the pins follow suit.
    // Restricting to the empty set, not `null`: `null` means "no restriction".
    if (this.dataset.demoDisabled != null) {
      this.#emit('shopla:pins:changed', { handles: [] });
      return;
    }

    const collection = this.#collection;
    const base = this.dataset.searchUrl;
    const shop = this.dataset.shop;
    if (!base || !shop) {
      this.#emit('shopla:pins:changed', { handles: null });
      return;
    }

    // A collection-scoped listing may run before the map has reported its
    // viewport (see connectedCallback): it lists the whole world then — the
    // collection bounds it, and the result is tiny — and that covers every
    // later box, so no viewport-sized re-listing follows.
    const box = this.#expandedBounds() ?? WORLD_BOUNDS;

    // `''` keys the no-collection (homepage) accumulation, so switching a
    // context collection on or off resets it like any other change.
    const contextKey = collection ?? '';
    if (contextKey !== this.#idleCollection) {
      this.#idleCollection = contextKey;
      this.#idleHandles.clear();
      this.#idleBoxes.clear();
    }

    // This box is already covered — re-hand the accumulated set to the map
    // (it may just have fallen back from a cleared search).
    if (this.#idleBoxes.has(box) || this.#idleBoxes.has(WORLD_BOUNDS)) {
      this.#emit('shopla:pins:changed', { handles: [...this.#idleHandles] });
      return;
    }

    // Only the latest listing matters (e.g. panning while one is in flight).
    this.#idleController?.abort();
    this.#idleController = new AbortController();
    const { signal } = this.#idleController;

    try {
      let handles;
      let places;

      if (collection) {
        const url = new URL(`${base.replace(/\/+$/, '')}/locations`);
        url.searchParams.set('s', shop);
        url.searchParams.set('bb', box);
        url.searchParams.set('coll', collection);

        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`shopla-search responded ${response.status}`);
        handles = this.#parseLocationHandles(await response.json());
        if (signal.aborted) return;
        // Only the handles the accumulation hasn't seen need resolving; the
        // rest are already on the map (and in the place cache).
        places = await this.#resolveLocations(
          handles.filter((handle) => !this.#idleHandles.has(handle)),
          signal
        );
      } else {
        const listed = await this.#fetchPlaces(
          { type: 'location', bb: box, size: String(PLACE_LOOKUP_CHUNK) },
          signal
        );
        // Distinguished from an empty viewport: a failed listing must fail
        // open, not blank every pin on the map.
        if (!listed) throw new Error('places listing failed');
        handles = listed.map((place) => place.handle);
        places = await this.#resolveLocations(
          handles.filter((handle) => !this.#idleHandles.has(handle)),
          signal,
          // The listing already names each location's shop, so the branding
          // fallback resolves in the same round trip rather than after it.
          listed.map((place) => place.shop_handle)
        );
      }
      if (signal.aborted) return;

      this.#idleBoxes.add(box);
      for (const handle of handles) this.#idleHandles.add(handle);
      // A search may have started while this was in flight — its results own
      // the pins now; the accumulation stays cached for the next clear.
      if (this.query) return;
      this.#emit('shopla:pins:changed', { handles: [...this.#idleHandles], places });
    } catch (error) {
      if (signal.aborted) return;
      console.warn('Shop-locations listing failed:', error);
      this.#emit('shopla:pins:changed', { handles: null });
    }
  }

  /**
   * Defensively extracts shop-location handles from a `/locations` response —
   * an array of `{ shop_location_handle, lat, lon, product_count }` entries.
   *
   * @param {unknown} data
   * @returns {string[]}
   */
  #parseLocationHandles(data) {
    const list = Array.isArray(data) ? data : (data?.locations ?? []);
    if (!Array.isArray(list)) return [];

    const handles = [];
    for (const item of list) {
      const handle = item == null ? null : (item.shop_location_handle ?? item.handle);
      if (typeof handle === 'string' && handle) handles.push(handle);
    }
    return handles;
  }

  /**
   * Loads one page of a shop location's matching products for the in-map
   * carousel: a `/search` scoped to the location handle, paginated by
   * `from`/`size`, with the product cards rendered via the predictive-search
   * section. Called directly by the map (`<mapbox-component>`), which owns the
   * carousel; returns the rendered card HTML plus whether another page may
   * exist. Supersedes any previous in-flight location load.
   *
   * A load that is itself superseded — by a newer one, by a search-context
   * change or by the query being cleared — REJECTS with an AbortError (see
   * `supersededError`) rather than resolving: it has no answer, whereas an
   * empty page is one ("this location has nothing for this query").
   *
   * @param {string} handle - The shop-location collection handle.
   * @param {number} [from] - Result offset (0-based).
   * @param {number} [size] - Page size.
   * @returns {Promise<{ products: { id: string, html: string, distance: number | null }[], hasMore: boolean }>}
   */
  async loadLocationProducts(handle, from = 0, size = CAROUSEL_PAGE) {
    const empty = { products: [], hasMore: false };
    if (!handle) return empty;

    // A page already loaded for this (unchanged) context is served from the
    // location cache — reopening a carousel (e.g. after panning away and
    // back) costs no request.
    const pageKey = `${from}:${size}`;
    const cached = this.#locationCache.get(handle);
    const cachedPage = cached?.pages.get(pageKey);
    if (cachedPage) {
      cached.lastSeen = ++this.#cacheSeq;
      return cachedPage;
    }

    // Only the latest carousel load matters (e.g. quickly switching locations).
    this.#locationController?.abort();
    this.#locationController = new AbortController();
    const { signal } = this.#locationController;

    const url = this.#buildSearchUrl({ coll: handle, from, size });
    if (url === null) return empty;

    // `fetch` rejects with an AbortError of its own when aborted mid-flight;
    // the checks after each await catch a supersession that landed between.
    const response = await fetch(url, { signal });
    if (signal.aborted) throw supersededError();
    if (!response.ok) throw new Error(`shopla-search responded ${response.status}`);

    const entries = this.#parseResults(await response.json());
    const cardsById = await this.#fetchProductCards(entries, signal);
    if (cardsById === null || signal.aborted) throw supersededError();

    const products = [];
    for (const entry of entries) {
      const card = cardsById.get(entry.id);
      if (!card) continue;
      // Every product in this carousel belongs to `handle`'s shop-location
      // collection — but only a PICKUP search scopes its link into it, so the
      // product page opens with the location context (address, opening hours,
      // local availability). A DELIVERY search keeps the bare /products/ URL:
      // the visitor is buying by mail, and the location-scoped page would
      // tell the pickup story of a door they never chose — the bare page
      // shows the delivery context (the ships-to row) instead.
      if (this.#mode !== 'shipping') this.#scopeCardLink(card, handle);
      products.push({ id: entry.id, html: card.outerHTML, distance: entry.distance });
    }

    // A full page came back → there may be another. (An exact-multiple last
    // page costs one extra empty fetch, which the caller handles gracefully.)
    const result = { products, hasMore: entries.length >= size };
    // Cache the page with its shop location, re-creating the bucket if it was
    // evicted; the context-change abort in #runSearch guarantees a stale
    // response never lands in a fresh cache.
    this.#touchLocation(handle, products[0]?.distance ?? null).pages.set(pageKey, result);
    return result;
  }

  /**
   * Loads one page of ONE SHOP's delivery-only products for the map's centred
   * carousel — the counterpart of `loadLocationProducts` for hits that have no
   * shop location to scope to. Called directly by the map, same contract —
   * including the AbortError rejection of a superseded load.
   *
   * No request to the search service: unlike a location carousel (whose
   * products are a `coll`-scoped slice the global search never fetched), the
   * delivery-only hits ARE the global search's own blank-handle results, so
   * paging is a slice of the shop's bucket and only the product cards have to
   * be rendered. That bounds the carousel to the delivery-only products within
   * the global page (`GLOBAL_SIZE`) — at CAROUSEL_PAGE per page, far more than
   * a visitor pages through.
   *
   * Their links are deliberately NOT collection-scoped: a delivery-only product
   * sits in no shop-location collection, so `/products/<handle>` is its only
   * correct URL.
   *
   * @param {string} shop - The shop handle whose bucket to page ("" for the
   *   unbranded bucket of hits with no known shop).
   * @param {number} [from] - Result offset (0-based).
   * @param {number} [size] - Page size.
   * @returns {Promise<{ products: { id: string, html: string, distance: number | null }[], hasMore: boolean }>}
   */
  async loadDeliveryProducts(shop, from = 0, size = CAROUSEL_PAGE) {
    const empty = { products: [], hasMore: false };

    const bucket = this.#deliveryEntries.get(shop);
    if (!bucket) return empty;

    const pageKey = `${shop}|${from}:${size}`;
    const cached = this.#deliveryPages.get(pageKey);
    if (cached) return cached;

    const slice = bucket.slice(from, from + size);
    if (slice.length === 0) return empty;

    // Superseded by a newer load the same way a location page is — a fresh
    // search aborts this controller before replacing the entries.
    this.#locationController?.abort();
    this.#locationController = new AbortController();
    const { signal } = this.#locationController;

    const cardsById = await this.#fetchProductCards(slice, signal);
    // Superseded meanwhile — the same contract as loadLocationProducts.
    if (cardsById === null || signal.aborted) throw supersededError();

    const products = [];
    for (const entry of slice) {
      const card = cardsById.get(entry.id);
      if (!card) continue;
      products.push({ id: entry.id, html: card.outerHTML, distance: entry.distance });
    }

    // `hasMore` is exact here (the whole result set is already in hand), unlike
    // the location carousel's "a full page came back, so maybe" heuristic.
    const result = { products, hasMore: from + size < bucket.length };
    this.#deliveryPages.set(pageKey, result);
    return result;
  }

  /**
   * Defensively extracts ranked results from the search response. Accepts a
   * bare array or an object wrapping one, where each item is an id or an
   * object carrying `product_id` / `id`, the `shop_location_handle` it matched
   * at, the `shop_handle` of its shop, and optionally `_distance` (meters,
   * present for location searches).
   *
   * `shop` is what a DELIVERY-ONLY hit is grouped and branded by — it has no
   * location handle to group by. A search service predating that field yields
   * "" here, which the map renders as one unbranded card (see #buildDeliveryCard).
   *
   * `productHandle` is the service's `handle` — the PRODUCT's storefront handle,
   * not to be confused with `handle`, which is its shop LOCATION. It is what
   * `#fetchProductCard` addresses the product by. A service predating the field
   * yields "" here, and such a hit simply renders no card.
   *
   * @param {unknown} data
   * @returns {{ id: string, productHandle: string, handle: string, shop: string, distance: number | null }[]}
   */
  #parseResults(data) {
    const list = Array.isArray(data) ? data : (data?.results ?? data?.products ?? data?.product_ids ?? []);
    if (!Array.isArray(list)) return [];

    /** @type {{ id: string, productHandle: string, handle: string, shop: string, distance: number | null }[]} */
    const entries = [];
    for (const item of list) {
      if (item == null) continue;

      const isObject = typeof item === 'object';
      const id = isObject ? (item.product_id ?? item.id ?? item.productId) : item;
      const value = id == null ? '' : `${id}`.trim();
      if (!value) continue;

      const handle = isObject ? (item.shop_location_handle ?? item.shopLocationHandle) : null;
      const productHandle = isObject ? (item.handle ?? item.productHandle) : null;
      const shop = isObject ? (item.shop_handle ?? item.shopHandle) : null;
      const distance = isObject ? (item._distance ?? item.distance) : null;
      entries.push({
        id: value,
        productHandle: typeof productHandle === 'string' ? productHandle : '',
        handle: typeof handle === 'string' ? handle : '',
        shop: typeof shop === 'string' ? shop : '',
        distance: typeof distance === 'number' ? distance : null,
      });
    }
    return entries;
  }

  /**
   * Fetches the product cards for a set of ranked entries, keyed by product id
   * so the caller can lay them out in ranking order. `null` if the load was
   * aborted (the caller treats that as superseded, never as "nothing here").
   *
   * One request per product, in parallel — each a direct render of that
   * product's own card on its own route (see `#fetchProductCard`).
   *
   * This used to be ONE request to Shopify's predictive search
   * (`/search/suggest?q=id:… OR id:…`), which is where the recurring
   * empty-carousel bug came from: that endpoint does not answer `id:` reliably.
   * Measured on staging, two back-to-back sweeps of the same 60 indexed
   * products: `id:` retrieved 15, then 17, the two sets differing in BOTH
   * directions; individual ids measured 0 and 2 hits out of 8 repeats. A
   * carousel page whose products all missed rendered empty under a shop card
   * the search service had correctly matched.
   *
   * The products are NOT missing from Shopify's index — a name search returns
   * them and their product pages are live. What is unreliable is asking that
   * search for a KNOWN product. Both one-request alternatives were implemented
   * and measured over the 12 shop cards of one `shirt` search, and both fail:
   *
   *   - `handle:… OR handle:…` here: 2 cards empty, 22 of 30 products. One
   *     batch of three repeated six times returned 2,2,3,3,2,2, and the same
   *     query gave all three at `resources[limit]=10` and NOTHING at `limit=3`.
   *   - `id:… OR id:…` on the regular /search endpoint: 6 cards empty, 15 of
   *     30, 334 KB. One failing set returned 0,3,0,0 over four tries; its ids
   *     fetched alone returned nothing. The full page fails identically, so it
   *     is not the section render.
   *
   * Each looked solid on the first id-set tried and fell over across twelve —
   * which is precisely how this bug survived three rounds of fixes.
   *
   * So the fix is not a better query, it is not querying: a product route is a
   * lookup and cannot half-answer. The cost is N small requests instead of one
   * — they run in parallel, the section renderer caches each by URL (a product
   * card is a stable resource, unlike a result page), and the carousel caches
   * the assembled page on top of that. Measured: 12 of 12 cards populated, 30
   * of 30 products rendered.
   *
   * @param {{ id: string, productHandle: string }[]} entries
   * @param {AbortSignal} signal
   * @returns {Promise<Map<string, HTMLElement> | null>}
   */
  async #fetchProductCards(entries, signal) {
    if (entries.length === 0) return new Map();

    const cards = await Promise.all(entries.map((entry) => this.#fetchProductCard(entry, signal)));
    if (signal.aborted) return null;

    /** @type {Map<string, HTMLElement>} */
    const byId = new Map();
    for (const card of cards) if (card) byId.set(card.id, card.node);
    return byId;
  }

  /**
   * Renders ONE product's card by fetching its own product route
   * (`/products/<handle>?section_id=shopla-product-card`), wrapped in the
   * `.map-search-results__card` `<li>` the map and the dropdown lay out.
   *
   * Never throws: a product that has gone away since it was indexed (404), a
   * network failure, or a handle the service could not supply all resolve to
   * `null` — one missing card, not a failed page. Each of those is logged,
   * because after this change a missing card means the index and the storefront
   * genuinely disagree, which is worth seeing rather than rendering as a gap.
   *
   * @param {{ id: string, productHandle: string }} entry
   * @param {AbortSignal} signal
   * @returns {Promise<{ id: string, node: HTMLElement } | null>}
   */
  async #fetchProductCard({ id, productHandle }, signal) {
    if (!productHandle) {
      console.warn(`map-search: no handle for product ${id} — its card cannot be rendered`);
      return null;
    }

    const url = new URL(`${localeRoot()}/products/${encodeURIComponent(productHandle)}`, location.origin);

    let html;
    try {
      html = await sectionRenderer.getSectionHTML(PRODUCT_CARD_SECTION, true, url, signal);
    } catch (error) {
      if (signal.aborted) return null;
      console.warn(`map-search: could not load the card for ${productHandle} —`, error);
      return null;
    }
    if (signal.aborted) return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Absent on anything that isn't the section: a 404 page (the product went
    // away since it was indexed) comes back as HTML with a 200-shaped body, so
    // the marker — not the status — is what says a card was rendered.
    const card = doc.querySelector('[data-shopla-product-card]');
    if (!card) {
      console.warn(`map-search: ${productHandle} rendered no card — gone from the storefront but still indexed?`);
      return null;
    }
    // A renamed handle redirects to whatever holds it now. Laying that out
    // would show a product the ranking never chose, under another one's id.
    const renderedId = card.getAttribute('data-shopla-product-id') ?? '';
    if (renderedId && renderedId !== id) {
      console.warn(`map-search: ${productHandle} resolved to product ${renderedId}, not ${id} — skipped`);
      return null;
    }

    const item = document.createElement('li');
    item.className = 'map-search-results__card';
    item.replaceChildren(...card.childNodes);
    return { id, node: item };
  }

  /**
   * Scopes a rendered product card's link to its shop-location collection, so the
   * card links to `/collections/<handle>/products/<handle>` (the `within` form).
   * No-op when the handle is missing or the card has no link.
   *
   * @param {Element} card - The rendered product card (`<li>`).
   * @param {string} handle - The shop-location collection handle.
   */
  #scopeCardLink(card, handle) {
    if (!handle) return;
    const link = card.querySelector('.resource-card__link');
    if (!link) return;
    const scoped = scopeProductUrl(link.getAttribute('href'), handle);
    if (scoped) link.setAttribute('href', scoped);
  }

  /**
   * Renders products into the no-map fallback dropdown: fetches the cards, lays
   * them out in the search service's ranking order in our own uniform grid
   * (.map-search-results), tags each with its distance, and shows the dropdown.
   *
   * @param {{ id: string, productHandle: string, handle: string, distance: number | null }[]} entries
   * @param {AbortSignal} signal
   */
  async #renderProducts(entries, signal) {
    const ranked = entries.slice(0, 8);

    const byId = await this.#fetchProductCards(ranked, signal);
    if (byId === null || signal.aborted) return;

    if (byId.size === 0) {
      this.#noResults(this.dataset.i18nEmpty ?? 'No products found');
      return;
    }

    // Lay the cards out in ranked order (the map skips this — it groups by
    // location instead).
    const list = document.createElement('ul');
    list.className = 'map-search-results list-unstyled';
    for (const entry of ranked) {
      const card = byId.get(entry.id);
      if (!card) continue;
      // Each result carries the shop-location collection it matched at; a
      // pickup search scopes its link into that collection. A delivery search
      // keeps the bare /products/ URL — the location-scoped product page
      // would show the pickup context, not the delivery one (see
      // loadLocationProducts).
      if (this.#mode !== 'shipping') this.#scopeCardLink(card, entry.handle);
      list.appendChild(card);
    }

    this.#applyDistanceTags(list, ranked);

    this.#showResults(list);
  }

  /**
   * Stamps each rendered card with a distance tag (location searches only —
   * the search service returns `_distance` when queried with `l`).
   *
   * @param {Element} list
   * @param {{ id: string, distance: number | null }[]} entries
   */
  #applyDistanceTags(list, entries) {
    const distanceById = new Map(entries.filter((entry) => entry.distance != null).map((entry) => [entry.id, entry.distance]));
    if (distanceById.size === 0) return;

    for (const card of list.querySelectorAll('.map-search-results__card')) {
      const idElement = card.querySelector('[data-product-id]');
      const distance = distanceById.get(idElement?.getAttribute('data-product-id') ?? '');
      if (distance == null) continue;

      // The resource-card is position: relative, so the tag can sit on the media.
      const target = card.querySelector('.resource-card') ?? card;
      const tag = document.createElement('span');
      tag.className = 'map-search__distance-tag';
      tag.textContent = this.#formatDistance(distance);
      target.appendChild(tag);
    }
  }

  /**
   * Formats a distance in meters for display: "850 m" below 1 km, "17.6 km"
   * above (no decimal from 100 km).
   *
   * @param {number} meters
   * @returns {string}
   */
  #formatDistance(meters) {
    if (meters < 1000) return `${Math.round(meters)} m`;

    const km = meters / 1000;
    return km < 100 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }

  /**
   * Shows rendered result cards in the no-map fallback dropdown. (On a map page
   * results are grouped by location and handed to the map instead — see
   * `#runSearch` / `loadLocationProducts`.)
   *
   * @param {Element} inner
   */
  #showResults(inner) {
    const { results } = this.refs;
    if (!results) return;
    results.classList.remove('is-loading');
    results.replaceChildren(inner);
    results.hidden = false;
  }

  /**
   * The "nothing matched" headline for the current search mode: a pickup
   * search is bounded by the map's viewport (zooming out widens it), a
   * delivery search by the visitor's address (panning cannot).
   *
   * @returns {string}
   */
  #emptyText() {
    return this.#mode === 'shipping'
      ? this.dataset.i18nEmptyDelivery ?? 'No product matching your search can be shipped to your shipping address.'
      : this.dataset.i18nEmptyPickup ?? 'No product in this area matches your search. Zoom out to widen your search.';
  }

  /**
   * The shop-registration prompt shown under an empty search result — the one
   * place it appears. Null when the storefront configured no link, so the
   * empty result then shows its headline alone.
   *
   * @returns {{ prompt: string, cta: string, url: string } | null}
   */
  #registerPrompt() {
    const url = this.dataset.registerUrl;
    if (!url) return null;
    return {
      prompt: this.dataset.i18nRegisterPrompt ?? '',
      cta: this.dataset.i18nRegisterCta ?? '',
      url,
    };
  }

  /**
   * Reports a genuine empty result — the query ran and matched nothing — to
   * the map pane or the fallback dropdown: a magnifier centred above `text`
   * (in bold), and the "Own a shop?" prompt under it where the storefront
   * configured a registration link.
   *
   * Distinct from `#status('empty', …)`, which also carries the "open the map
   * to search" hint. That is not an empty result, and inviting the visitor to
   * register a shop there would be a non-sequitur — hence the `noResults` flag
   * the map routes on.
   *
   * @param {string} text
   */
  #noResults(text) {
    const register = this.#registerPrompt();

    if (this.#useMapResults) {
      this.#emit('shopla:search:status', {
        state: 'empty',
        text,
        query: this.query,
        noResults: true,
        register,
      });
      return;
    }

    const { results } = this.refs;
    if (!results) return;

    // Mirrors the map pane's card (see mapbox.js #showPaneNoResults) down to
    // the class-name split.
    const message = document.createElement('div');
    message.className = 'map-search__results-message map-search__empty';
    const icon = document.createElement('span');
    icon.className = 'map-search__empty-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = SEARCH_ICON_SVG;
    message.appendChild(icon);
    const title = document.createElement('p');
    title.className = 'map-search__empty-title';
    title.textContent = text;
    message.appendChild(title);
    if (register) message.appendChild(buildRegisterPrompt(register, 'map-search__empty-register'));

    results.classList.remove('is-loading');
    results.replaceChildren(message);
    results.hidden = false;
  }

  /**
   * Routes a plain status message (loading / empty hint / error) to the map
   * pane or the fallback dropdown. An empty RESULT goes through #noResults.
   *
   * @param {'loading' | 'empty' | 'error'} state
   * @param {string} text
   */
  #status(state, text) {
    if (this.#useMapResults) {
      // Carry the query so the map can tell a new-query status (which may
      // re-open a merchant-closed pane) from a same-query one (which must not).
      this.#emit('shopla:search:status', { state, text, query: this.query });
      return;
    }

    const { results } = this.refs;
    if (!results) return;

    const message = document.createElement('div');
    message.className = 'map-search__results-message';
    message.textContent = text;

    results.classList.toggle('is-loading', state === 'loading');
    results.replaceChildren(message);
    results.hidden = false;
  }

  /** Clears the results (map pane or fallback dropdown). */
  #clear() {
    if (this.#useMapResults) {
      this.#emit('shopla:search:cleared', {});
      return;
    }

    const { results } = this.refs;
    if (!results) return;
    results.classList.remove('is-loading');
    results.replaceChildren();
    results.hidden = true;
  }

  /**
   * Renders the demo-storefront "try it live" notice in the results area — the
   * map's results pane (via a `demo` status event the map renders where a "no
   * shops" message would go) or the fallback dropdown with no map — in place of
   * the results a real query would show. Driven by the `data-demo-*` attributes
   * (see map-search-bar.liquid); no backend request is made. Kept in sync with
   * the shopla-search app extension.
   */
  #showDemoNotice() {
    const demo = {
      title: this.dataset.i18nDemoTitle ?? '',
      body: this.dataset.i18nDemoBody ?? '',
      url: this.dataset.demoUrl ?? '',
      cta: this.dataset.i18nDemoCta ?? '',
    };
    if (this.#useMapResults) {
      // The map owns its results pane; hand it the notice content so it renders
      // there instead of a shop-location list / empty-result message.
      this.#emit('shopla:search:status', { state: 'demo', query: this.query, demo });
      return;
    }
    const { results } = this.refs;
    if (!results) return;
    results.classList.remove('is-loading');
    results.replaceChildren(this.#buildDemoNotice(demo));
    results.hidden = false;
  }

  /**
   * Builds the demo notice element (title + body + optional production link)
   * for the fallback dropdown. The map renders its own copy in the pane (see
   * mapbox.js #showPaneDemoNotice) with matching class names.
   *
   * @param {{ title: string, body: string, url: string, cta: string }} demo
   * @returns {HTMLElement}
   */
  #buildDemoNotice({ title, body, url, cta }) {
    const notice = document.createElement('div');
    notice.className = 'map-search__results-message map-search__demo-notice';
    if (title) {
      const el = document.createElement('p');
      el.className = 'map-search__demo-notice-title';
      el.textContent = title;
      notice.appendChild(el);
    }
    if (body) {
      const el = document.createElement('p');
      el.className = 'map-search__demo-notice-body';
      el.textContent = body;
      notice.appendChild(el);
    }
    if (url && cta) {
      const link = document.createElement('a');
      link.className = 'map-search__demo-notice-cta';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = cta;
      notice.appendChild(link);
    }
    return notice;
  }

  /**
   * Type guard for a persisted current location: an object with finite
   * lat/lng and a known source. (A legacy persisted `center` has no source,
   * so it can never sneak in as a location.)
   *
   * @param {unknown} value
   * @returns {value is { lat: number, lng: number, source: 'manual' | 'geolocation' | 'ip' }}
   */
  #isLocation(value) {
    return (
      value != null &&
      typeof value === 'object' &&
      Number.isFinite(/** @type {any} */ (value).lat) &&
      Number.isFinite(/** @type {any} */ (value).lng) &&
      LOCATION_SOURCE_RANK[/** @type {any} */ (value).source] != null
    );
  }

  /**
   * Reads the persisted search preferences from localStorage. Best-effort:
   * returns an empty object when storage is unavailable or the value is junk.
   *
   * @returns {{ mode?: string, location?: { lat: number, lng: number, source: string } | null, shipsTo?: string, shipsToRegion?: string }}
   */
  #readPersisted() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const data = JSON.parse(raw);
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  }

  /**
   * Persists the current mode, current location, shipping zone (with its
   * region display name) and shipping address selection. Best-effort — storage may
   * be unavailable (private
   * mode / quota), in which case we simply skip it. (Search RESULTS are
   * deliberately not persisted — their cache lives in memory and dies with
   * the page.)
   */
  #persist() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          mode: this.#mode,
          location: this.#location,
          shipsTo: this.#shipsTo,
          shipsToRegion: this.#shipsToRegion,
          shipsToKey: this.#resolvedKey,
          delivery: this.#delivery,
        })
      );
    } catch {
      // Persistence is best-effort; ignore storage failures.
    }
  }

  /**
   * Dispatches a bubbling search event for the map to handle.
   *
   * @param {string} type
   * @param {object} detail
   */
  #emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }
}

if (!customElements.get('map-search-component')) {
  customElements.define('map-search-component', MapSearchComponent);
}
